import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { useSearchParams, useNavigate, Link, useParams } from 'react-router-dom';
import NavBar from '../../components/NavBar';
import UniversitySearch from '../../components/UniversitySearch';
import CustomSelect from '../../components/CustomSelect';
import DateRangePicker from '../../components/DateRangePicker';
import BackgroundDoodles from '../../components/BackgroundDoodles';
import VoteModal from '../../components/VoteModal';

import GlassCapsulePopup from '../../components/GlassCapsulePopup/GlassCapsulePopup';
import { API_ENDPOINTS, API_BASE_URL, FEATURE_FLAGS, UNIVERSITY_CACHE } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useGrid } from '../../contexts/GridContext';
import { updateSEO, generateHomeSEO, generateCollegeSEO } from '../../utils/seo';
import { getUniversityLogo } from '../../utils/universityLogos';
import {
    getCityByUniversityId,
    getCityByFilterName,
    getCityListingUniversityId,
    matchSupportedCityFromInput,
    appendListingLocationParams,
} from '../../config/cities';
import { getStateByUniversityId } from '../../config/states';
import { SUPPORTED_UNIVERSITY_IDS, TAMPA_METRO_UNIVERSITY_IDS, NYC_METRO_UNIVERSITY_IDS } from '../../config/supportedUniversities';
import { getCampusByUniversityId, getCampusesInBounds, getNearestCampuses } from '../../config/universityCampuses';
import { findUniversityBySlug, getUniversitySlug } from '../../utils/slug';
// Chrome flicker fix: Import ImageCarousel directly instead of lazy loading
// Lazy loading with per-card Suspense boundaries caused Chrome to skip painting some cards
import ImageCarousel from '../../components/ImageCarousel';
import ImageWithFallback from '../../components/ImageWithFallback';
import './Home.css';

// Lazy load non-critical components (not ImageCarousel - it's used for every listing)
const Footer = lazy(() => import('../../components/Footer'));
const MapboxListingsMap = lazy(() => import('../../components/Map/MapboxListingsMap'));

interface Listing {
    id: string;
    title: string;
    description: string;
    unit_type: string;
    address: string;
    latitude?: number | null;
    longitude?: number | null;
    monthly_rent: number;
    pictures: string[];
    compressed_pictures?: string[];
    lease_term: string;
    lease_start_date?: string;
    lease_end_date?: string;
    university_id: number;
    city?: string;
    university_name?: string;
    university_abbreviation?: string;
    parking?: boolean | string;
    allow_pets?: boolean | string;
    gym?: boolean | string;
    pool?: boolean | string;
    laundry?: boolean | string;
    gender_preference?: string;
    verification_status?: string;
    created_at: string;
    user_id?: string;
    user_first_name?: string;
    user_last_name?: string;
    user_avatar_url?: string;
    source?: string;
    facebook_url?: string;
    facebook_first_name?: string;
    facebook_last_name?: string;
    facebook_avatar_url?: string;
    user_student_verification_status?: 'unverified' | 'pending' | 'approved' | 'rejected';
    // Alt-listing fields
    is_alt_listing?: boolean;
    facebook_person_name?: string;
    facebook_profile_picture_url?: string;
    is_boosted?: boolean;
}

interface University {
    id: number;
    name: string;
    location: string;
    abbreviation: string;
}

type MapBounds = {
    north: number;
    south: number;
    east: number;
    west: number;
};

// Helper function to generate thumbnail URL from full URL (for backward compatibility)
const getThumbnailUrl = (fullUrl: string): string => {
    // If URL already contains _thumb, return as is
    if (fullUrl.includes('_thumb')) {
        return fullUrl;
    }

    // Try to replace the filename pattern to get thumbnail
    // Pattern: .../uuid.jpg -> .../uuid_thumb.jpg
    const urlParts = fullUrl.split('/');
    const filename = urlParts[urlParts.length - 1];
    if (filename && filename.includes('.')) {
        const nameWithoutExt = filename.split('.')[0];
        const extension = filename.split('.').slice(1).join('.');
        const thumbnailFilename = `${nameWithoutExt}_thumb.${extension}`;
        urlParts[urlParts.length - 1] = thumbnailFilename;
        return urlParts.join('/');
    }

    // If pattern doesn't match, return original URL
    return fullUrl;
};

const isListingFeatured = (listing: Listing): boolean =>
    listing.is_boosted === true || String(listing.is_boosted).toLowerCase() === 'true';

const MONTHS = [
    { value: '', label: 'Select month' },
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
];

const Home = () => {
    const { slug } = useParams<{ slug: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    // Get university param directly from URL for immediate detection on SPA navigation
    const universityParam = searchParams.get('university');
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth(); // Destructure user and isAuthenticated
    const { showGrid } = useGrid();

    // Core listing states
    const [allListings, setAllListings] = useState<Listing[]>([]);
    const [filteredListings, setFilteredListings] = useState<Listing[]>([]);
    const [displayedListings, setDisplayedListings] = useState<Listing[]>([]);


    const [universities, setUniversities] = useState<University[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    // Pagination states
    const [hasMore, setHasMore] = useState(false);
    const [nextToken, setNextToken] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState<number | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    // Default to list view on mobile/compact, or double view on desktop
    const [showMapOnMobile, setShowMapOnMobile] = useState(false);
    const [mobileListItemsCount, setMobileListItemsCount] = useState(8); // Start with 8 items on mobile list view
    const [selectedListingId, setSelectedListingId] = useState<string | null>(null);
    const [selectedFromMap, setSelectedFromMap] = useState<boolean>(false);
    const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
    /** Listings loaded by panning the map — shown on the map only, not in the sidebar. */
    const [mapBrowseListings, setMapBrowseListings] = useState<Listing[]>([]);
    const mapBrowseFetchedKeysRef = useRef<Set<string>>(new Set());
    const mapBrowseFetchGenRef = useRef(0);
    const mapBoundsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [mapViewportKey, setMapViewportKey] = useState(0);
    const [mobileMapSelectionIds, setMobileMapSelectionIds] = useState<string[]>([]);
    const [mobileMapDismissKey, setMobileMapDismissKey] = useState(0);
    // Optimized for scalability: fetch maximum allowed by backend initially
    // This ensures we show many listings upfront and efficiently paginate for thousands more
    const INITIAL_FETCH_LIMIT = 100; // Match backend's maximum limit for efficient initial load
    const ITEMS_PER_PAGE = 100; // Use maximum page size for subsequent loads (optimal for thousands of listings)
    const currentFetchUniversityRef = useRef<string>('');
    const prevFiltersRef = useRef<string>('');

    // Filter states - initialize from URL parameters
    const [selectedUniversity, setSelectedUniversity] = useState<string>(searchParams.get('university') || '');
    // Pending university state for landing page - only applied when Search button is clicked
    const [pendingUniversity, setPendingUniversity] = useState<string>('');
    // Landing page university input text (used to resolve a "valid university" even if user didn't click a suggestion)
    const [pendingUniversityInput, setPendingUniversityInput] = useState<string>('');
    // Pending terms for landing page - only applied when Search button is clicked
    const [pendingTerms, setPendingTerms] = useState<string[]>([]);
    const [selectedTerms, setSelectedTerms] = useState<string[]>(() => {
        const termParam = searchParams.get('term');
        if (termParam) {
            // Support both single term (comma-separated) and array format
            return termParam.split(',').filter(t => t.trim() !== '');
        }
        return [];
    });
    const [priceSort, setPriceSort] = useState<string>(searchParams.get('sort') || '');
    const [selectedUnitType, setSelectedUnitType] = useState<string>(searchParams.get('unitType') || '');
    const [maxPrice] = useState<string>('');
    const [parkingRequired, setParkingRequired] = useState<boolean>(searchParams.get('parking') === 'true');
    const [petsAllowed, setPetsAllowed] = useState<boolean>(searchParams.get('pets') === 'true');
    const [gymRequired, setGymRequired] = useState<boolean>(searchParams.get('gym') === 'true');
    const [poolRequired, setPoolRequired] = useState<boolean>(searchParams.get('pool') === 'true');
    const [laundryRequired, setLaundryRequired] = useState<boolean>(searchParams.get('laundry') === 'true');
    const [genderPreference, setGenderPreference] = useState<string>(searchParams.get('gender') || '');
    const [selectedStartDate, setSelectedStartDate] = useState<string>(searchParams.get('startDate') || '');
    const [selectedEndDate, setSelectedEndDate] = useState<string>(searchParams.get('endDate') || '');
    
    // Pending dates for landing page
    const [pendingStartDate, setPendingStartDate] = useState<string>('');
    const [pendingEndDate, setPendingEndDate] = useState<string>('');
    
    const [isMobile, setIsMobile] = useState<boolean>(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
    const [showFiltersDropdown, setShowFiltersDropdown] = useState<boolean>(false);
    /** Location searches hide campus pins until enabled in Filters. */
    const [showUniversitiesOnMap, setShowUniversitiesOnMap] = useState(false);
    
    const [activeTabRect, setActiveTabRect] = useState<{ left: number; width: number }>({ left: 0, width: 0 });

    const [isWideDesktop, setIsWideDesktop] = useState<boolean>(typeof window !== 'undefined' ? window.innerWidth > 1100 : false);
    // When in search view (URL has university or slug), use current window size for layout so first paint is correct after SPA nav
    const effectiveWideDesktop = (universityParam || slug) ? (typeof window !== 'undefined' && window.innerWidth > 1100) : isWideDesktop;
    const effectiveMobile = (universityParam || slug) ? (typeof window !== 'undefined' && window.innerWidth <= 768) : isMobile;
    const isCompactLayout = effectiveMobile || !effectiveWideDesktop;
    // Desktop: 12 items per page (3 per row = 4 rows), Mobile: 16 items per page
    const PAGE_SIZE = useMemo(() => (!isMobile ? 12 : 16), [isMobile]);
    const [isSearchFixed, setIsSearchFixed] = useState(false);
    const [showComingSoon, setShowComingSoon] = useState(false);
    const [searchTriggered, setSearchTriggered] = useState(false);
    // Increment to force a fresh fetch even if filters didn't change (e.g., re-search same university)
    const [searchRequestKey, setSearchRequestKey] = useState(0);
    const [showNoListingsPopup, setShowNoListingsPopup] = useState(false);
    const [isNoListingsPopupFadingOut, setIsNoListingsPopupFadingOut] = useState(false);
    const [isCheckingListings, setIsCheckingListings] = useState(false);
    const [hasVerifiedListings, setHasVerifiedListings] = useState(false);
    const [isUnsupportedUniversity, setIsUnsupportedUniversity] = useState(false); // Track if searched university is not supported
    const [showVoteModal, setShowVoteModal] = useState(false);
    const [showVoteThanksPopup, setShowVoteThanksPopup] = useState(false);
    const [isVoteThanksFadingOut, setIsVoteThanksFadingOut] = useState(false);
    const [votePrepopulatedUniversity, setVotePrepopulatedUniversity] = useState('');
    const isCheckingListingsRef = useRef(false);
    const lastNoListingsSearchRef = useRef<string | null>(null); // Track last search with no listings
    const [isUniversityInputValid, setIsUniversityInputValid] = useState(true); // Track if typed university is valid
    const [showValidationError, setShowValidationError] = useState(false); // Show validation error popup
    const [retryCount, setRetryCount] = useState(0); // Track retry attempts for listings
    const landingHeadlineRef = useRef<HTMLHeadingElement | null>(null);
    const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [landingFeaturedListings, setLandingFeaturedListings] = useState<Listing[]>([]);
    const [loadingLandingFeatured, setLoadingLandingFeatured] = useState<boolean>(true);



    // Track if we've already resolved the university from the slug to avoid infinite loops
    const slugResolvedRef = useRef<string | undefined>(undefined);

    // Resolve university from slug if present in URL
    useEffect(() => {
        if (slug && universities.length > 0 && slugResolvedRef.current !== slug) {
            const foundUni = findUniversityBySlug(universities, slug);
            if (foundUni) {
                const fullUni = universities.find((u: University) =>
                    u.name === foundUni.name || u.abbreviation === foundUni.abbreviation
                );
                if (fullUni) {
                    setSelectedUniversity(fullUni.id.toString());
                    setSelectedTerms([]); // Reset to all semesters
                    setPendingTerms([]);
                    setSearchTriggered(true);
                    // Critical for re-using the Home component:
                    // Trigger the results view and listing verification
                    setIsCheckingListings(true);
                    isCheckingListingsRef.current = true;
                    setHasVerifiedListings(false);
                    setShowNoListingsPopup(false);
                    slugResolvedRef.current = slug;
                }
            }
        } else if (!slug) {
            slugResolvedRef.current = undefined;
        }
    }, [slug, universities]);

    // Developer tools state - now managed by GridContext

    // Apply show-grid class to body when debugging is enabled
    useEffect(() => {
        if (showGrid) {
            document.body.classList.add('show-grid');
        } else {
            document.body.classList.remove('show-grid');
        }

        // Cleanup on unmount
        return () => {
            document.body.classList.remove('show-grid');
        };
    }, [showGrid]);

    // Chrome-only flicker mitigation for heavy search results rendering
    useEffect(() => {
        const ua = navigator.userAgent;
        const isChrome = /Chrome/.test(ua) && !/Edg|OPR|Brave/.test(ua);
        if (isChrome) {
            document.body.classList.add('chrome-flicker-fix');
        }

        return () => {
            document.body.classList.remove('chrome-flicker-fix');
        };
    }, []);

    // Photo gallery states for each listing
    const [listingPhotoIndices, setListingPhotoIndices] = useState<{ [key: string]: number }>({});
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd] = useState<number | null>(null);
    const [touchMove, setTouchMove] = useState<number | null>(null);
    const [isDragging, setIsDragging] = useState<boolean>(false);

    const listingRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    // Chrome flicker fix: These values are now constants to prevent re-renders
    const allCompressedLoaded = true;
    const animatedListingIds = new Set<string>();

    const listTopRef = useRef<HTMLDivElement>(null);
    const listPanelRef = useRef<HTMLDivElement>(null);
    const headerAndFiltersRef = useRef<HTMLDivElement | null>(null);
    const lockedScrollYRef = useRef<number>(0);
    const filterButtonRef = useRef<HTMLButtonElement>(null);
    const [filterButtonRect, setFilterButtonRect] = useState<DOMRect | null>(null);

    // Update filter button position when dropdown opens
    useEffect(() => {
        if (showFiltersDropdown && filterButtonRef.current) {
            setFilterButtonRect(filterButtonRef.current.getBoundingClientRect());
        }
    }, [showFiltersDropdown]);

    const scrollToTop = useCallback(() => {
        requestAnimationFrame(() => {
            const smoothBehavior = document.body.classList.contains('chrome-flicker-fix') ? 'auto' : 'smooth';
            if (listPanelRef.current) {
                listPanelRef.current.scrollTo({ top: 0, behavior: smoothBehavior });
            }
            window.scrollTo({ top: 0, behavior: smoothBehavior });
        });
    }, []);

    const handleMapBoundsChange = useCallback((bounds: MapBounds) => {
        // Sidebar stays on the original search; only the map loads nearby regions.
        setMapBounds(bounds);
    }, []);

    // When the user pans/zooms the map away from their search, load listings for
    // campuses in view onto the map only (sidebar stays on the search results).
    useEffect(() => {
        const searchActive = !!selectedUniversity || searchTriggered || !!slug || !!universityParam;
        if (!mapBounds || !searchActive) return;

        if (mapBoundsDebounceRef.current) {
            clearTimeout(mapBoundsDebounceRef.current);
        }

        mapBoundsDebounceRef.current = setTimeout(() => {
            const campuses = getCampusesInBounds(mapBounds, { limit: 8 });
            if (campuses.length === 0) return;

            const selectedId = selectedUniversity ? Number(selectedUniversity) : NaN;
            const skipUniIds = new Set<number>();
            const skipCityFilters = new Set<string>();

            if (!Number.isNaN(selectedId)) {
                if (selectedId > 0) {
                    skipUniIds.add(selectedId);
                } else {
                    const state = getStateByUniversityId(selectedId);
                    if (state) {
                        state.universityIds.forEach((id) => skipUniIds.add(id));
                        if (state.universityIds.some((id) => TAMPA_METRO_UNIVERSITY_IDS.includes(id))) {
                            skipCityFilters.add('Tampa');
                        }
                        if (state.universityIds.some((id) => NYC_METRO_UNIVERSITY_IDS.includes(id))) {
                            skipCityFilters.add('New York City');
                        }
                    } else {
                        const city = getCityByUniversityId(selectedId);
                        if (city?.cityFilter) {
                            skipCityFilters.add(city.cityFilter);
                        }
                        if (city) {
                            skipUniIds.add(getCityListingUniversityId(city));
                        }
                    }
                }
            }

            type BrowseKey =
                | { type: 'city'; key: string; city: string }
                | { type: 'university'; key: string; universityId: number };

            const keysToFetch: BrowseKey[] = [];
            const seen = new Set<string>();

            for (const campus of campuses) {
                let cityFilter: string | undefined;
                if (TAMPA_METRO_UNIVERSITY_IDS.includes(campus.id)) {
                    cityFilter = 'Tampa';
                } else if (NYC_METRO_UNIVERSITY_IDS.includes(campus.id)) {
                    cityFilter = 'New York City';
                }

                let browseKey: BrowseKey;
                if (cityFilter) {
                    if (skipCityFilters.has(cityFilter)) continue;
                    browseKey = { type: 'city', key: `city:${cityFilter}`, city: cityFilter };
                } else {
                    if (skipUniIds.has(campus.id)) continue;
                    browseKey = {
                        type: 'university',
                        key: `university:${campus.id}`,
                        universityId: campus.id,
                    };
                }

                if (seen.has(browseKey.key) || mapBrowseFetchedKeysRef.current.has(browseKey.key)) {
                    continue;
                }
                seen.add(browseKey.key);
                keysToFetch.push(browseKey);
            }

            if (keysToFetch.length === 0) return;

            const fetchGen = mapBrowseFetchGenRef.current;

            const fetchOne = async (browseKey: BrowseKey): Promise<Listing[]> => {
                mapBrowseFetchedKeysRef.current.add(browseKey.key);

                const url = new URL(API_ENDPOINTS.SUBLEASES.LIST);
                url.searchParams.append('limit', '100');
                if (browseKey.type === 'city') {
                    url.searchParams.set('city', browseKey.city);
                } else {
                    url.searchParams.set('university_id', String(browseKey.universityId));
                }
                if (selectedStartDate) url.searchParams.append('start_date', selectedStartDate);
                if (selectedEndDate) url.searchParams.append('end_date', selectedEndDate);

                const altUrl = new URL(API_ENDPOINTS.ALT_LISTINGS.LIST);
                altUrl.searchParams.append('limit', '100');
                if (browseKey.type === 'city') {
                    altUrl.searchParams.set('city', browseKey.city);
                } else {
                    altUrl.searchParams.set('university_id', String(browseKey.universityId));
                }
                if (selectedStartDate) altUrl.searchParams.append('start_date', selectedStartDate);
                if (selectedEndDate) altUrl.searchParams.append('end_date', selectedEndDate);

                try {
                    const [response, altResponse] = await Promise.all([
                        fetch(url.toString()),
                        fetch(altUrl.toString()),
                    ]);

                    let listings: Listing[] = [];
                    if (response.ok) {
                        const data = await response.json();
                        listings = (data.data || []).map((listing: any) => ({
                            ...listing,
                            university_id:
                                typeof listing.university_id === 'string'
                                    ? parseInt(listing.university_id, 10)
                                    : listing.university_id,
                            is_alt_listing: false,
                        }));
                    }
                    if (altResponse.ok) {
                        const altData = await altResponse.json();
                        const altListings = (altData.data || []).map((listing: any) => ({
                            ...listing,
                            university_id:
                                typeof listing.university_id === 'string'
                                    ? parseInt(listing.university_id, 10)
                                    : listing.university_id,
                            is_alt_listing: true,
                            facebook_url: listing.facebook_url,
                            facebook_person_name: listing.facebook_person_name,
                            facebook_profile_picture_url: listing.facebook_profile_picture_url,
                        }));
                        listings = [...listings, ...altListings];
                    }
                    return listings.filter(
                        (l) =>
                            l.verification_status !== 'pending' &&
                            typeof l.latitude === 'number' &&
                            typeof l.longitude === 'number',
                    );
                } catch (error) {
                    console.error('Error fetching map-browse listings:', error);
                    // Allow retry on next pan if this request failed.
                    mapBrowseFetchedKeysRef.current.delete(browseKey.key);
                    return [];
                }
            };

            (async () => {
                const batches = keysToFetch.slice(0, 6);
                const results = await Promise.all(batches.map(fetchOne));
                if (fetchGen !== mapBrowseFetchGenRef.current) return;

                const merged = results.flat();
                if (merged.length === 0) return;

                setMapBrowseListings((prev) => {
                    const seenIds = new Set(prev.map((l) => l.id));
                    const next = [...prev];
                    for (const listing of merged) {
                        if (!seenIds.has(listing.id)) {
                            seenIds.add(listing.id);
                            next.push(listing);
                        }
                    }
                    return next;
                });
            })();
        }, 400);

        return () => {
            if (mapBoundsDebounceRef.current) {
                clearTimeout(mapBoundsDebounceRef.current);
                mapBoundsDebounceRef.current = null;
            }
        };
    }, [mapBounds, selectedUniversity, selectedStartDate, selectedEndDate, searchTriggered, slug, universityParam]);


    // Photo navigation functions
    const getCurrentPhotoIndex = (listingId: string) => {
        return listingPhotoIndices[listingId] || 0;
    };

    const nextPhoto = (listingId: string, totalPhotos: number) => {
        setListingPhotoIndices(prev => ({
            ...prev,
            [listingId]: ((prev[listingId] || 0) + 1) % totalPhotos
        }));
    };

    const prevPhoto = (listingId: string, totalPhotos: number) => {
        setListingPhotoIndices(prev => ({
            ...prev,
            [listingId]: ((prev[listingId] || 0) - 1 + totalPhotos) % totalPhotos
        }));
    };

    const setPhotoIndex = (listingId: string, index: number) => {
        setListingPhotoIndices(prev => ({
            ...prev,
            [listingId]: index
        }));
    };

    // Touch event handlers for smooth swipe functionality
    const handleTouchStart = (listingId: string, e: React.TouchEvent) => {
        e.stopPropagation();
        setTouchStart(e.targetTouches[0].clientX);
        setTouchMove(e.targetTouches[0].clientX);
        setIsDragging(true);
    };

    const handleTouchMove = (listingId: string, e: React.TouchEvent) => {
        if (!isDragging || !touchStart) return;
        e.preventDefault();
        const currentX = e.targetTouches[0].clientX;
        setTouchMove(currentX);
    };

    const handleTouchEnd = (listingId: string, totalPhotos: number, e: React.TouchEvent) => {
        e.stopPropagation();
        if (!touchStart || !touchMove || totalPhotos <= 1) {
            setIsDragging(false);
            setTouchStart(null);
            setTouchMove(null);
            return;
        }

        const distance = touchStart - touchMove;
        const threshold = 80;
        const isLeftSwipe = distance > threshold;
        const isRightSwipe = distance < -threshold;

        if (isLeftSwipe) {
            nextPhoto(listingId, totalPhotos);
        } else if (isRightSwipe) {
            prevPhoto(listingId, totalPhotos);
        }

        setIsDragging(false);
        setTouchStart(null);
        setTouchMove(null);
    };

    // Load filters from URL params on mount (before initial data fetch)
    useEffect(() => {
        const urlUniversity = searchParams.get('university');
        const urlCity = searchParams.get('city');
        const urlTerm = searchParams.get('term');
        const urlSort = searchParams.get('sort');
        const urlUnitType = searchParams.get('unitType');
        const urlParking = searchParams.get('parking');
        const urlPets = searchParams.get('pets');
        const urlGym = searchParams.get('gym');
        const urlPool = searchParams.get('pool');
        const urlGender = searchParams.get('gender');
        const urlStartDate = searchParams.get('startDate');
        const urlEndDate = searchParams.get('endDate');

        // If URL params exist, use them
        if (urlUniversity || urlCity || urlTerm || urlSort || urlUnitType || urlParking || urlPets || urlGym || urlPool || urlGender || urlStartDate || urlEndDate) {
            if (urlCity) {
                const cityFromUrl = getCityByFilterName(urlCity);
                setSelectedUniversity(cityFromUrl ? String(cityFromUrl.universityId) : (urlUniversity || ''));
            } else {
                setSelectedUniversity(urlUniversity || '');
            }
            if (urlTerm) {
                setSelectedTerms(urlTerm.split(',').filter(t => t.trim() !== ''));
            }
            setPriceSort(urlSort || '');
            setSelectedUnitType(urlUnitType || '');
            setParkingRequired(urlParking === 'true');
            setPetsAllowed(urlPets === 'true');
            setGymRequired(urlGym === 'true');
            setPoolRequired(urlPool === 'true');
            setGenderPreference(urlGender || '');
            setSelectedStartDate(urlStartDate || '');
            setSelectedEndDate(urlEndDate || '');

            // Reloading with URL filters should re-run the search automatically
            setSearchTriggered(true);

            isCheckingListingsRef.current = true;
            setIsCheckingListings(true);
            setHasVerifiedListings(false);
            setShowNoListingsPopup(false);
            setIsUnsupportedUniversity(false);
        }
        filtersLoadedRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount

    // Sync filters to URL params whenever they change
    useEffect(() => {

        // Update URL params to match current filters
        const newParams = new URLSearchParams();
        if (selectedUniversity) {
            const valInt = parseInt(selectedUniversity, 10);
            const city = getCityByUniversityId(valInt);
            if (city?.cityFilter) {
                newParams.set('city', city.cityFilter);
            } else {
                newParams.set('university', selectedUniversity);
            }
        }
        if (selectedTerms.length > 0) newParams.set('term', selectedTerms.join(','));
        if (priceSort) newParams.set('sort', priceSort);
        if (selectedUnitType) newParams.set('unitType', selectedUnitType);
        if (parkingRequired) newParams.set('parking', 'true');
        if (petsAllowed) newParams.set('pets', 'true');
        if (gymRequired) newParams.set('gym', 'true');
        if (poolRequired) newParams.set('pool', 'true');
        if (laundryRequired) newParams.set('laundry', 'true');
        if (genderPreference) newParams.set('gender', genderPreference);
        if (selectedStartDate) newParams.set('startDate', selectedStartDate);
        if (selectedEndDate) newParams.set('endDate', selectedEndDate);

        // CRITICAL: Preserve impersonation token if present so AuthContext can read it
        // Home.tsx would otherwise strip it out immediately, causing ghost login to fail
        const currentImpersonationToken = searchParams.get('impersonation_token');
        if (currentImpersonationToken) {
            newParams.set('impersonation_token', currentImpersonationToken);
        }

        // Guard: only update the URL if it actually changed.
        // This prevents router-level churn and avoids triggering expensive rerenders.
        const nextSearch = newParams.toString();
        const currentSearch = searchParams.toString();
        if (nextSearch !== currentSearch) {
            setSearchParams(newParams, { replace: true });
        }
    }, [selectedUniversity, selectedTerms, priceSort, selectedUnitType, parkingRequired, petsAllowed, gymRequired, poolRequired, laundryRequired, genderPreference, selectedStartDate, selectedEndDate, setSearchParams, searchParams]);

    // Update SEO
    useEffect(() => {
        if (slug && universities.length > 0) {
            const foundUni = findUniversityBySlug(universities, slug);
            if (foundUni) {
                const seoData = generateCollegeSEO(
                    foundUni.name,
                    foundUni.abbreviation || '',
                    totalCount || 0,
                    slug
                );
                updateSEO(seoData);
                return;
            }
        }

        // Default home SEO if no slug or uni not found
        updateSEO(generateHomeSEO(totalCount || 0));
    }, [slug, universities, totalCount]);


    // Track if initial load has completed
    const initialLoadRef = useRef(false);
    const filtersLoadedRef = useRef(false);
    const initialFetchDoneRef = useRef(false);

    // Fetch more listings from the backend
    const fetchMoreListings = useCallback(async (isInitial: boolean = false, universityId?: string) => {
        if (!isInitial && (!hasMore || loadingMore)) return;
        if (!isInitial && !nextToken) {
            setHasMore(false);
            return;
        }

        // Use the provided universityId or fall back to selectedUniversity
        const currentUniversity = universityId !== undefined ? universityId : selectedUniversity;

        // Track the current fetch to prevent stale updates
        currentFetchUniversityRef.current = currentUniversity;

        try {
            // Set loading to true for initial fetches to show skeleton
            if (isInitial) {
                setLoading(true);
            }
            setLoadingMore(true);

            // Build URL with pagination
            const url = new URL(API_ENDPOINTS.SUBLEASES.LIST);
            url.searchParams.append('limit', ITEMS_PER_PAGE.toString());
            if (isInitial) {
                url.searchParams.append('include_total_count', 'true');
            }
            if (!isInitial && nextToken) {
                url.searchParams.append('last_evaluated_key', nextToken);
            }
            if (currentUniversity) {
                appendListingLocationParams(url.searchParams, currentUniversity);
            }
            if (selectedStartDate) {
                url.searchParams.append('start_date', selectedStartDate);
            }
            if (selectedEndDate) {
                url.searchParams.append('end_date', selectedEndDate);
            }

            // Parallelize fetching of both regular and alt listings for significant speed gain
            const regularFetchPromise = fetch(url.toString());
            
            const altUrl = new URL(API_ENDPOINTS.ALT_LISTINGS.LIST);
            altUrl.searchParams.append('limit', ITEMS_PER_PAGE.toString());
            if (currentUniversity) {
                appendListingLocationParams(altUrl.searchParams, currentUniversity);
            }
            if (selectedStartDate) altUrl.searchParams.append('start_date', selectedStartDate);
            if (selectedEndDate) altUrl.searchParams.append('end_date', selectedEndDate);
            const altFetchPromise = fetch(altUrl.toString());

            const [response, altResponse] = await Promise.all([regularFetchPromise, altFetchPromise]);

            if (!response.ok) {
                console.error('Failed to fetch listings:', response.status, response.statusText);
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Fetched listings data:', { count: data.data?.length || 0, data, universityId: currentUniversity });

            let newListings = (data.data || []).map((listing: any) => ({
                ...listing,
                university_id: typeof listing.university_id === 'string' ? parseInt(listing.university_id) : listing.university_id,
                is_alt_listing: false
            }));

            // Process alt-listings if successful
            if (altResponse.ok) {
                const altData = await altResponse.json();
                const altListings = (altData.data || []).map((listing: any) => ({
                    ...listing,
                    university_id: typeof listing.university_id === 'string' ? parseInt(listing.university_id) : listing.university_id,
                    is_alt_listing: true,
                    facebook_url: listing.facebook_url,
                    facebook_person_name: listing.facebook_person_name,
                    facebook_profile_picture_url: listing.facebook_profile_picture_url
                }));
                // Merge alt-listings with regular listings
                newListings = [...newListings, ...altListings];
                // Sort by boosted status first, then by created_at descending (newest first)
                newListings.sort((a: Listing, b: Listing) => {
                    // Prioritize boosted listings first, then most recent
                    const aBoosted = isListingFeatured(a);
                    const bBoosted = isListingFeatured(b);
                    if (aBoosted && !bBoosted) return -1;
                    if (!aBoosted && bBoosted) return 1;
                    
                    const dateA = new Date(a.created_at || 0).getTime();
                    const dateB = new Date(b.created_at || 0).getTime();
                    return dateB - dateA;
                });
            }

            const pagination = data.pagination || {};

            console.log('Processed listings:', { count: newListings.length, newListings, universityId: currentUniversity });

            // Only update state if this fetch is still relevant (university hasn't changed during fetch)
            if (currentFetchUniversityRef.current === currentUniversity) {
                setAllListings(prev => {
                    if (isInitial) {
                        return newListings;
                    }
                    const seen = new Set(prev.map((listing: Listing) => listing.id));
                    const dedupedNext = newListings.filter((listing: Listing) => !seen.has(listing.id));
                    return [...prev, ...dedupedNext];
                });
                setNextToken(pagination.next_token || null);
                setHasMore(pagination.has_more || false);
                // Update total count if provided (only on initial fetch)
                if (isInitial && pagination.total_count !== undefined) {
                    setTotalCount(pagination.total_count);
                }

                // Check for no listings AFTER fetch completes (only on initial search fetch)
                // This is the authoritative check - we know exactly what the API returned
                if (isInitial && isCheckingListingsRef.current) {
                    if (newListings.length === 0) {
                        // No listings from backend - check if university is supported
                        const universityIdNum = currentUniversity ? parseInt(currentUniversity) : null;
                        const isSupported = universityIdNum !== null && (
                            SUPPORTED_UNIVERSITY_IDS.includes(universityIdNum) ||
                            Boolean(getCityByUniversityId(universityIdNum)) ||
                            Boolean(getStateByUniversityId(universityIdNum))
                        );

                        setShowNoListingsPopup(true);
                        setIsNoListingsPopupFadingOut(false);
                        setHasVerifiedListings(false);
                        setIsUnsupportedUniversity(!isSupported);
                    } else {
                        // Has listings - verify and allow transition to map view
                        setShowNoListingsPopup(false);
                        setHasVerifiedListings(true);
                        setIsUnsupportedUniversity(false);
                        // Reset retry count since we found listings
                        setRetryCount(0);
                    }
                    
                    // Always track that we've checked this university to prevent infinite retry loops
                    // regardless of whether we found 0 items or items that were filtered out locally.
                    lastNoListingsSearchRef.current = currentUniversity;

                    setIsCheckingListings(false);
                    isCheckingListingsRef.current = false;


                }
            }

        } catch (error) {
            console.error('Error fetching more listings:', error);
            // Set empty array on error to show "No listings" instead of infinite loading
            if (isInitial && currentFetchUniversityRef.current === currentUniversity) {
                setAllListings([]);
                // Also handle the checking state on error
                if (isCheckingListingsRef.current) {
                    const universityIdNum = currentUniversity ? parseInt(currentUniversity) : null;
                    const isSupported = universityIdNum !== null && (
                        SUPPORTED_UNIVERSITY_IDS.includes(universityIdNum) ||
                        Boolean(getCityByUniversityId(universityIdNum)) ||
                        Boolean(getStateByUniversityId(universityIdNum))
                    );

                    setShowNoListingsPopup(true);
                    setIsNoListingsPopupFadingOut(false);
                    setHasVerifiedListings(false);
                    setIsUnsupportedUniversity(!isSupported);
                    setIsCheckingListings(false);
                    isCheckingListingsRef.current = false;
                }
            }
        } finally {

            // Set loading to false when initial fetch completes
            if (isInitial) {
                setLoading(false);
            }
            setLoadingMore(false);
        }
    }, [hasMore, loadingMore, nextToken, selectedUniversity, selectedStartDate, selectedEndDate]);

    // Fetch initial data (Universities and listings in parallel)
    useEffect(() => {
        // Wait for filters to be loaded from localStorage before fetching
        if (!filtersLoadedRef.current) return;
        // Only run once on initial load
        if (initialFetchDoneRef.current) return;

        const fetchInitialData = async () => {
            try {
                setLoading(true);
                initialFetchDoneRef.current = true;

                // Only fetch listings if there's an active search (university selected or filters applied)
                const hasActiveSearch = !!selectedUniversity ||
                    selectedTerms.length > 0 ||
                    selectedUnitType ||
                    maxPrice ||
                    parkingRequired ||
                    petsAllowed ||
                    gymRequired ||
                    poolRequired ||
                    genderPreference;

                // Start fetching listings only if there's an active search (don't wait for universities)
                const listingsPromise = hasActiveSearch ? (async () => {
                    try {
                        const url = new URL(API_ENDPOINTS.SUBLEASES.LIST);
                        // Use smaller initial fetch for faster load time
                        url.searchParams.append('limit', INITIAL_FETCH_LIMIT.toString());
                        url.searchParams.append('include_total_count', 'true');
                        if (selectedUniversity) {
                            appendListingLocationParams(url.searchParams, selectedUniversity);
                        }
                        if (selectedStartDate) {
                            url.searchParams.append('start_date', selectedStartDate);
                        }
                        if (selectedEndDate) {
                            url.searchParams.append('end_date', selectedEndDate);
                        }
                        const response = await fetch(url.toString());
                        if (response.ok) {
                            const data = await response.json();
                            const newListings = (data.data || []).map((listing: any) => ({
                                ...listing,
                                university_id: typeof listing.university_id === 'string' ? parseInt(listing.university_id) : listing.university_id
                            }));
                            const pagination = data.pagination || {};
                            setAllListings(newListings);
                            setNextToken(pagination.next_token || null);
                            setHasMore(pagination.has_more || false);
                            // Set total count from API response
                            if (pagination.total_count !== undefined) {
                                setTotalCount(pagination.total_count);
                            }

                            // Mirror fetchMoreListings: clear the "checking" gate so the map
                            // can become visible after remount (e.g. listing → back → map).
                            if (isCheckingListingsRef.current) {
                                if (newListings.length === 0) {
                                    const universityIdNum = selectedUniversity ? parseInt(selectedUniversity, 10) : null;
                                    const isSupported = universityIdNum !== null && (
                                        SUPPORTED_UNIVERSITY_IDS.includes(universityIdNum) ||
                                        Boolean(getCityByUniversityId(universityIdNum)) ||
                                        Boolean(getStateByUniversityId(universityIdNum))
                                    );
                                    setShowNoListingsPopup(true);
                                    setIsNoListingsPopupFadingOut(false);
                                    setHasVerifiedListings(false);
                                    setIsUnsupportedUniversity(!isSupported);
                                } else {
                                    setShowNoListingsPopup(false);
                                    setHasVerifiedListings(true);
                                    setIsUnsupportedUniversity(false);
                                    setRetryCount(0);
                                }
                                lastNoListingsSearchRef.current = selectedUniversity;
                                setIsCheckingListings(false);
                                isCheckingListingsRef.current = false;
                            }

                            // Stop loading as soon as listings are available
                            setLoading(false);
                        } else {
                            setAllListings([]);
                            if (isCheckingListingsRef.current) {
                                const universityIdNum = selectedUniversity ? parseInt(selectedUniversity, 10) : null;
                                const isSupported = universityIdNum !== null && (
                                    SUPPORTED_UNIVERSITY_IDS.includes(universityIdNum) ||
                                    Boolean(getCityByUniversityId(universityIdNum)) ||
                                    Boolean(getStateByUniversityId(universityIdNum))
                                );
                                setShowNoListingsPopup(true);
                                setIsNoListingsPopupFadingOut(false);
                                setHasVerifiedListings(false);
                                setIsUnsupportedUniversity(!isSupported);
                                setIsCheckingListings(false);
                                isCheckingListingsRef.current = false;
                            }
                            setLoading(false);
                        }
                    } catch (error) {
                        console.error('Error fetching initial listings:', error);
                        setAllListings([]);
                        if (isCheckingListingsRef.current) {
                            const universityIdNum = selectedUniversity ? parseInt(selectedUniversity, 10) : null;
                            const isSupported = universityIdNum !== null && (
                                SUPPORTED_UNIVERSITY_IDS.includes(universityIdNum) ||
                                Boolean(getCityByUniversityId(universityIdNum)) ||
                                Boolean(getStateByUniversityId(universityIdNum))
                            );
                            setShowNoListingsPopup(true);
                            setIsNoListingsPopupFadingOut(false);
                            setHasVerifiedListings(false);
                            setIsUnsupportedUniversity(!isSupported);
                            setIsCheckingListings(false);
                            isCheckingListingsRef.current = false;
                        }
                        setLoading(false);
                    }
                })() : Promise.resolve();

                // Fetch universities in parallel (non-blocking) with caching
                const universitiesPromise = (async () => {
                    try {
                        // Check cache first
                        const cached = localStorage.getItem(UNIVERSITY_CACHE.KEY);
                        if (cached) {
                            try {
                                const { timestamp, universities } = JSON.parse(cached);
                                if (Date.now() - timestamp < UNIVERSITY_CACHE.EXPIRY) {
                                    setUniversities(universities);
                                    // Optionally still fetch in background to refresh cache
                                    fetch(API_ENDPOINTS.UNIVERSITIES.LIST)
                                        .then(res => res.json())
                                        .then(data => {
                                            const unis = data.data || [];
                                            setUniversities(unis);
                                            localStorage.setItem(UNIVERSITY_CACHE.KEY, JSON.stringify({
                                                timestamp: Date.now(),
                                                universities: unis
                                            }));
                                        }).catch(() => {});
                                    return;
                                }
                            } catch (e) {
                                localStorage.removeItem(UNIVERSITY_CACHE.KEY);
                            }
                        }

                        const universitiesResponse = await fetch(API_ENDPOINTS.UNIVERSITIES.LIST);
                        const universitiesData = await universitiesResponse.json();
                        const unis = universitiesData.data || [];
                        setUniversities(unis);
                        localStorage.setItem(UNIVERSITY_CACHE.KEY, JSON.stringify({
                            timestamp: Date.now(),
                            universities: unis
                        }));
                    } catch (error) {
                        console.error('Error fetching universities:', error);
                        setUniversities([]);
                    }
                })();

                // Wait for both, but listings will show as soon as they're ready
                await Promise.all([listingsPromise, universitiesPromise]);

                // If no active search, set loading to false immediately
                if (!hasActiveSearch) {
                    setLoading(false);
                }
            } catch (error) {
                console.error('Error fetching initial data:', error);
                setUniversities([]);
                setAllListings([]);
                setLoading(false);
            } finally {
                initialLoadRef.current = true;
            }
        };

        fetchInitialData();
    }, [selectedUniversity]); // Run when selectedUniversity changes (after filters are loaded)

    // Fetch listings when selectedUniversity changes (after initial load)
    useEffect(() => {
        // Skip if initial load hasn't completed yet
        if (!initialLoadRef.current) return;

        // Only fetch if there's an active search
        const hasActiveSearch = !!selectedUniversity ||
            selectedTerms.length > 0 ||
            selectedUnitType ||
            maxPrice ||
            parkingRequired ||
            petsAllowed ||
            gymRequired ||
            poolRequired ||
            genderPreference;

        // Only fetch new data if a search was initiated
        // Allow fetching all universities when searchTriggered is true with no university selected
        const shouldFetch = !!selectedUniversity || searchTriggered;

        // Set loading BEFORE clearing listings to prevent flash
        if (shouldFetch) {
            setLoading(true);
        }

        // Clear existing listings and pagination when university changes
        setAllListings([]);
        setMapBrowseListings([]);
        mapBrowseFetchedKeysRef.current = new Set();
        mapBrowseFetchGenRef.current += 1;
        setMapBounds(null);
        setNextToken(null);
        setHasMore(false);
        setTotalCount(null);
        setMobileListItemsCount(8); // Reset mobile list items count when filters change

        if (shouldFetch) {
            // Fetch new data with the current university
            // Pass universityId explicitly to avoid stale closures
            const universityToFetch = selectedUniversity;
            fetchMoreListings(true, universityToFetch);
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUniversity, selectedTerms, priceSort, selectedUnitType, maxPrice, parkingRequired, petsAllowed, gymRequired, poolRequired, laundryRequired, genderPreference, selectedStartDate, selectedEndDate, searchTriggered, searchRequestKey]); // fetchMoreListings intentionally excluded to prevent re-triggers

    // Disable browser scroll restoration on mount and force scroll to top
    useEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
        window.scrollTo(0, 0);

        return () => {
            if ('scrollRestoration' in window.history) {
                window.history.scrollRestoration = 'auto';
            }
        };
    }, []);

    // Viewport: sync on mount, resize, visibility, and when map view is active so desktop navbar always shows.
    const syncViewport = useCallback(() => {
        setIsMobile(window.innerWidth <= 768);
        setIsWideDesktop(window.innerWidth > 1100);
    }, []);
    useLayoutEffect(() => {
        syncViewport();
        const handleResize = () => syncViewport();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [syncViewport]);
    useLayoutEffect(() => {
        const mqWide = window.matchMedia('(min-width: 1101px)');
        const mqMobile = window.matchMedia('(max-width: 768px)');
        const onMatch = () => syncViewport();
        mqWide.addEventListener('change', onMatch);
        mqMobile.addEventListener('change', onMatch);
        return () => {
            mqWide.removeEventListener('change', onMatch);
            mqMobile.removeEventListener('change', onMatch);
        };
    }, [syncViewport]);
    useLayoutEffect(() => {
        const onVisibility = () => {
            if (document.visibilityState === 'visible') syncViewport();
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, [syncViewport]);

    // Note: we intentionally do NOT auto-disable map view on desktop.

    useEffect(() => {
        if (isMobile && !showMapOnMobile) {
            setSelectedListingId(null);
            setSelectedFromMap(false);
        }
    }, [isMobile, showMapOnMobile]);

    // Helper to check if listing has images
    const listingHasImages = useCallback((listing: Listing) => {
        if (listing.source === 'facebook') {
            return listing.pictures && Array.isArray(listing.pictures) && listing.pictures.length > 0;
        } else {
            return listing.pictures && Array.isArray(listing.pictures) && listing.pictures.length > 0;
        }
    }, []);

    // Helper to check if listing has at least 2 images
    const listingHasTwoOrMoreImages = useCallback((listing: Listing) => {
        if (listing.source === 'facebook') {
            return listing.pictures && Array.isArray(listing.pictures) && listing.pictures.length >= 2;
        } else {
            return listing.pictures && Array.isArray(listing.pictures) && listing.pictures.length >= 2;
        }
    }, []);




    // Chrome flicker fix: Removed image loading useEffect entirely
    // allCompressedLoaded is now always true to prevent state-related re-renders

    // Filter and sort listings whenever data or filter values change
    // IMPORTANT: Backend already sorts by created_at (newest first)
    // We must preserve this order and only apply additional filtering/sorting on top
    useEffect(() => {
        // Filter out pending listings first
        const activeListings = allListings.filter(listing => listing.verification_status !== 'pending');
        const selectedUniversityId = selectedUniversity ? Number(selectedUniversity) : null;

        // Apply filters to all listings
        // CRITICAL: Do NOT re-sort unless user explicitly chose a sort option
        // Backend already sorted by created_at (newest first)
        let filteredListings = activeListings;

        // Filter by university - only when a university is actually selected
        if (selectedUniversityId !== null) {
            filteredListings = filteredListings.filter(listing => {
                const selectedState = getStateByUniversityId(selectedUniversityId);
                if (selectedState) {
                    const listingUniversityId = Number(listing.university_id);
                    return (
                        !Number.isNaN(listingUniversityId) &&
                        selectedState.universityIds.includes(listingUniversityId)
                    );
                }
                const selectedCity = getCityByUniversityId(selectedUniversityId);
                if (selectedCity?.cityFilter === 'Tampa') {
                    const listingUniId = Number(listing.university_id);
                    const isTampaUni = Number.isNaN(listingUniId) || TAMPA_METRO_UNIVERSITY_IDS.includes(listingUniId);

                    const addressLower = (listing.address || "").toLowerCase();
                    const titleLower = (listing.title || "").toLowerCase();
                    const isOutsideTampa = addressLower.includes("berkeley") ||
                                           addressLower.includes("seattle") ||
                                           addressLower.includes("boston") ||
                                           addressLower.includes("gainesville") ||
                                           addressLower.includes("orlando") ||
                                           addressLower.includes("miami") ||
                                           addressLower.includes("culver city") ||
                                           addressLower.includes("ca 90") ||
                                           addressLower.includes("wa 98") ||
                                           addressLower.includes("ma 02") ||
                                           titleLower.includes("berkeley") ||
                                           titleLower.includes("seattle") ||
                                           titleLower.includes("boston") ||
                                           titleLower.includes("uf ") ||
                                           titleLower.includes("ucf ");

                    return listing.city === 'Tampa' && isTampaUni && !isOutsideTampa;
                }
                if (selectedCity?.cityFilter === 'New York City') {
                    const listingUniId = Number(listing.university_id);
                    const isNycUni = Number.isNaN(listingUniId) || NYC_METRO_UNIVERSITY_IDS.includes(listingUniId);

                    const addressLower = (listing.address || "").toLowerCase();
                    const isOutsideNyc = addressLower.includes("tampa") ||
                                         addressLower.includes("lutz") ||
                                         addressLower.includes("gainesville") ||
                                         addressLower.includes("seattle") ||
                                         addressLower.includes("berkeley") ||
                                         addressLower.includes("ca 9") ||
                                         addressLower.includes("wa 9");

                    return listing.city === 'New York City' && isNycUni && !isOutsideNyc;
                }
                if (selectedCity) {
                    const targetUniId = getCityListingUniversityId(selectedCity);
                    const listingUniversityId = Number(listing.university_id);
                    return !Number.isNaN(listingUniversityId) && listingUniversityId === targetUniId;
                }
                const listingUniversityId = Number(listing.university_id);
                return !Number.isNaN(listingUniversityId) && listingUniversityId === selectedUniversityId;
            });
        }

        // Filter by term (multiple terms allowed)
        if (selectedTerms.length > 0) {
            filteredListings = filteredListings.filter(listing => {
                if (!listing.lease_term) return false;
                // Handle comma-separated terms sent from backend (e.g. "fall,spring")
                const listingTerms = listing.lease_term.split(',').map(t => t.trim().toLowerCase());
                // Show listing if ANY of the selected terms match ANY of the listing's terms
                return selectedTerms.some(term => listingTerms.includes(term.toLowerCase()));
            });
        }

        // Filter by date range
        if (selectedStartDate || selectedEndDate) {
            filteredListings = filteredListings.filter(listing => {
                const term = (listing.lease_term || '').toLowerCase();
                
                // Use explicit dates OR infer from term
                let listStart = listing.lease_start_date;
                let listEnd = listing.lease_end_date;

                if (!listStart || !listEnd) {
                    if (term.includes('spring')) {
                        listStart = listStart || '2026-01-01';
                        listEnd = listEnd || '2026-05-31';
                    } else if (term.includes('summer')) {
                        listStart = listStart || '2026-06-01';
                        listEnd = listEnd || '2026-07-31';
                    } else if (term.includes('fall')) {
                        listStart = listStart || '2026-08-01';
                        listEnd = listEnd || '2026-12-31';
                    }
                }

                if (!listStart) return false;
                
                // Backend-consistent "inclusive interval" logic (must cover entire requested range)
                if (selectedStartDate && !selectedEndDate) {
                    // Available on selectedStartDate
                    return listStart <= selectedStartDate && (listEnd ? listEnd >= selectedStartDate : true);
                }
                
                if (selectedStartDate && selectedEndDate) {
                    // Must fully cover [selectedStartDate, selectedEndDate]
                    return listStart <= selectedStartDate && (listEnd ? listEnd >= selectedEndDate : true);
                }

                return true;
            });
        }




        // Filter by unit type
        if (selectedUnitType) {
            filteredListings = filteredListings.filter(listing => listing.unit_type === selectedUnitType);
        }

        // Filter by max price
        if (maxPrice && !isNaN(Number(maxPrice))) {
            const maxPriceNum = Number(maxPrice);
            filteredListings = filteredListings.filter(listing => listing.monthly_rent <= maxPriceNum);
        }

        // Filter by parking requirement
        if (parkingRequired) {
            filteredListings = filteredListings.filter(listing => listing.parking === true || String(listing.parking).toLowerCase() === "true");
        }

        // Filter by pets allowed
        if (petsAllowed) {
            filteredListings = filteredListings.filter(listing => listing.allow_pets === true || String(listing.allow_pets).toLowerCase() === "true");
        }

        // Filter by gym requirement
        if (gymRequired) {
            filteredListings = filteredListings.filter(listing => listing.gym === true || String(listing.gym).toLowerCase() === "true");
        }

        // Filter by pool requirement
        if (poolRequired) {
            filteredListings = filteredListings.filter(listing => listing.pool === true || String(listing.pool).toLowerCase() === "true");
        }

        // Filter by laundry requirement
        if (laundryRequired) {
            filteredListings = filteredListings.filter(listing => listing.laundry === true || String(listing.laundry).toLowerCase() === "true");
        }

        // Filter by gender preference
        if (genderPreference) {
            filteredListings = filteredListings.filter(listing =>
                listing.gender_preference === genderPreference ||
                listing.gender_preference === 'no_preference'
            );
        }

        // ONLY apply custom sorting if user explicitly selected a price sort
        // Otherwise, preserve the backend's sort order (created_at, newest first)
        if (priceSort === 'low-to-high') {
            // Stable sort: secondary sort by created_at to maintain consistent order for same prices
            filteredListings = [...filteredListings].sort((a, b) => {
                const priceDiff = a.monthly_rent - b.monthly_rent;
                if (priceDiff !== 0) return priceDiff;
                // Same price: sort by created_at (newest first) for stability
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            });
        } else if (priceSort === 'high-to-low') {
            // Stable sort: secondary sort by created_at to maintain consistent order for same prices
            filteredListings = [...filteredListings].sort((a, b) => {
                const priceDiff = b.monthly_rent - a.monthly_rent;
                if (priceDiff !== 0) return priceDiff;
                // Same price: sort by created_at (newest first) for stability
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            });
        }
        // If priceSort is '' or 'recent', keep the backend's order (already sorted by created_at)
        // DO NOT re-sort here - it would break pagination consistency

        let finalListings = filteredListings;

        // Guard: avoid resetting state when the list didn't materially change.
        // Re-setting triggers a full rerender of every card/carousel (perceived as “flashing”).
        setFilteredListings(prev => {
            if (prev.length !== finalListings.length) return finalListings;
            for (let i = 0; i < prev.length; i++) {
                if (prev[i]?.id !== finalListings[i]?.id) return finalListings;
            }
            return prev;
        });

        // Note: The "no listings" check is now handled in fetchMoreListings callback
        // to avoid race conditions with state updates. This ensures we check only
        // AFTER the API response is received, not when allListings is temporarily empty.

        // Reset popup/verification when search is cleared (no active search)
        const searchActive = !!selectedUniversity || searchTriggered;
        if (!searchActive) {
            setHasVerifiedListings(false);
            setShowNoListingsPopup(false);
            setIsUnsupportedUniversity(false);
            isCheckingListingsRef.current = false;
        }

        const currentFilters = JSON.stringify({
            selectedUniversity,
            selectedTerms,
            priceSort,
            selectedUnitType,
            maxPrice,
            parkingRequired,
            petsAllowed,
            gymRequired,
            poolRequired,
            laundryRequired,
            genderPreference,
            selectedStartDate,
            selectedEndDate
        });

        const isFilterChange = prevFiltersRef.current !== currentFilters;
        if (isFilterChange) {
            setCurrentPage(1);
            setSelectedListingId(null);
            setSelectedFromMap(false);
            setMapViewportKey(prev => prev + 1);
            setMapBounds(null);
            setMapBrowseListings([]);
            mapBrowseFetchedKeysRef.current = new Set();
            mapBrowseFetchGenRef.current += 1;
            // Chrome flicker fix: Removed setAnimatedListingIds call
            scrollToTop();
            prevFiltersRef.current = currentFilters;
        }
    }, [allListings, selectedUniversity, selectedTerms, priceSort, selectedUnitType, maxPrice, parkingRequired, petsAllowed, gymRequired, poolRequired, laundryRequired, genderPreference, selectedStartDate, selectedEndDate, scrollToTop, searchTriggered]);

    useEffect(() => {
        // On mobile list view, use mobileListItemsCount instead of pagination
        if (isMobile && !showMapOnMobile) {
            const newDisplayedListings = filteredListings.slice(0, mobileListItemsCount);

            // Chrome flicker fix: Don't remove listings from animatedListingIds
            // Once a listing has been animated, keep it animated to prevent CSS class toggling
            // which causes Chrome-specific flickering issues

            setDisplayedListings(newDisplayedListings);
            return;
        }

        // Desktop "Load More" behavior (match mobile): show all items up to the current page size.
        const end = currentPage * PAGE_SIZE;
        const newDisplayedListings = filteredListings.slice(0, end);

        // Chrome flicker fix: Don't remove listings from animatedListingIds
        // Once a listing has been animated, keep it animated to prevent CSS class toggling
        // which causes Chrome-specific flickering issues

        setDisplayedListings(newDisplayedListings);
    }, [filteredListings, currentPage, PAGE_SIZE, hasMore, isMobile, showMapOnMobile, mobileListItemsCount]);

    // Chrome flicker fix: Removed animation tracking useEffect
    // This was causing re-renders via setAnimatedListingIds and setSkipInitialAnimation
    // Animations are now disabled (hasBeenAnimated is always true)

    // Force animation restart when a listing is selected from the map
    useEffect(() => {
        if (!selectedListingId) return;

        // Don't add green glow if selected from map
        if (selectedFromMap) return;

        if (document.body.classList.contains('chrome-flicker-fix')) {
            return;
        }

        // Small delay to ensure React has applied the class first
        const timer = setTimeout(() => {
            const target = listingRefs.current[selectedListingId];
            if (!target) return;

            // Force animation restart by removing and re-adding the class
            // This is safe because we're only manipulating the animation, not React state
            target.classList.remove('listing-card-highlight');
            // Re-add the class on the next frame to trigger the animation
            requestAnimationFrame(() => {
                target.classList.add('listing-card-highlight');
            });
        }, 10);

        return () => clearTimeout(timer);
    }, [selectedListingId, selectedFromMap]);

    useEffect(() => {
        // For mobile list view, check if we need to fetch more listings
        if (isMobile && !showMapOnMobile) {
            if (filteredListings.length < mobileListItemsCount && hasMore && !loadingMore && nextToken) {
                fetchMoreListings(false);
            }
            // If backend says there's more but no token, stop trying to fetch
            if (hasMore && !nextToken && !loadingMore) {
                setHasMore(false);
            }
            return;
        }

        // Desktop pagination fetch logic
        const neededCount = currentPage * PAGE_SIZE;
        if (filteredListings.length < neededCount && hasMore && !loadingMore && nextToken) {
            fetchMoreListings(false);
        }
        // If backend says there's more but no token, stop trying to fetch
        if (hasMore && !nextToken && !loadingMore) {
            setHasMore(false);
        }
    }, [currentPage, filteredListings.length, hasMore, loadingMore, nextToken, fetchMoreListings, PAGE_SIZE, isMobile, showMapOnMobile, mobileListItemsCount]);

    const handlePageChange = (page: number) => {
        if (page < 1) return;

        // Calculate the maximum valid page based on available listings
        const maxValidPage = filteredListings.length > 0
            ? Math.ceil(filteredListings.length / PAGE_SIZE)
            : 1;

        // If trying to go beyond the last page and there are no more items to fetch, cap at last page
        if (page > maxValidPage && !hasMore) {
            setCurrentPage(maxValidPage);
        } else {
            setCurrentPage(page);
        }

        setSelectedListingId(null);
        setSelectedFromMap(false);
        // Chrome flicker fix: Removed setAnimatedListingIds call
        scrollToTop();
    };

    const handleLoadMore = () => {
        // Mobile increments by 8; desktop increments by one "page" (16) but keeps a single list (no page UI).
        if (isMobile) {
            setMobileListItemsCount(prev => prev + 8);
        } else {
            setCurrentPage(prev => prev + 1);
        }
    };

    const buildBackUrl = () => {
        const params = new URLSearchParams();
        if (selectedUniversity) params.set('university', selectedUniversity);
        if (selectedTerms.length > 0) params.set('term', selectedTerms.join(','));
        if (priceSort) params.set('sort', priceSort);
        if (selectedUnitType) params.set('unitType', selectedUnitType);
        if (parkingRequired) params.set('parking', 'true');
        if (petsAllowed) params.set('pets', 'true');
        if (genderPreference) params.set('gender', genderPreference);

        const queryString = params.toString();
        return queryString ? `/?${queryString}` : '/';
    };

    const getListingUrl = (listingId: string) => {
        const backUrl = buildBackUrl();
        return `/listing/${listingId}?back=${encodeURIComponent(backUrl)}`;
    };

    const navigateToListing = (listingId: string) => {
        navigate(getListingUrl(listingId));
    };

    const handleMarkerClick = useCallback((listingId: string) => {
        // Chrome flicker fix: Removed setAnimatedListingIds call
        // Set selected listing from map (no scroll animation or green glow)
        setSelectedFromMap(true);
        setSelectedListingId(listingId);
        // Don't scroll or highlight when clicking on map in desktop split view
    }, []);

    const handleMapGroupSelection = useCallback((listingIds: string[]) => {
        setMobileMapSelectionIds(listingIds);
    }, []);

    const handleMobileSelectionClose = useCallback(() => {
        setMobileMapSelectionIds([]);
        setSelectedListingId(null);
        setSelectedFromMap(false);
        setMobileMapDismissKey((prev) => prev + 1);
    }, []);

    useEffect(() => {
        // Only clear selections when switching away from map on mobile
        // On desktop, selections should persist as map is always visible
        if (isMobile && !showMapOnMobile) {
            setMobileMapSelectionIds([]);
        }
    }, [isMobile, showMapOnMobile]);

    // DISABLED: This was preventing all scrolling - no longer needed with new layout
    /*
    useEffect(() => {
        // Prevent background page scroll while using the full-screen map view (mobile-style).
        if (!showMapOnMobile) return;

        const body = document.body;
        const html = document.documentElement;

        lockedScrollYRef.current = window.scrollY || window.pageYOffset || 0;

        const prevBody = {
            position: body.style.position,
            top: body.style.top,
            left: body.style.left,
            right: body.style.right,
            width: body.style.width,
            overflow: body.style.overflow,
        };
        const prevHtmlOverflow = html.style.overflow;

        html.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = '0';
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';

        return () => {
            html.style.overflow = prevHtmlOverflow;
            body.style.position = prevBody.position;
            body.style.top = prevBody.top;
            body.style.left = prevBody.left;
            body.style.right = prevBody.right;
            body.style.width = prevBody.width;
            body.style.overflow = prevBody.overflow;
            window.scrollTo(0, lockedScrollYRef.current);
        };
    }, [showMapOnMobile]);
    */

    useLayoutEffect(() => {
        if (!showMapOnMobile) {
            document.documentElement.style.removeProperty('--home-mobile-map-top');
            return;
        }

        const updateMapTop = () => {
            const el = headerAndFiltersRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const top = Math.max(0, Math.round(rect.bottom));
            document.documentElement.style.setProperty('--home-mobile-map-top', `${top}px`);
        };

        updateMapTop();
        const raf = requestAnimationFrame(updateMapTop);
        window.addEventListener('resize', updateMapTop);
        window.addEventListener('orientationchange', updateMapTop);
        const ro = new ResizeObserver(updateMapTop);
        if (headerAndFiltersRef.current) {
            ro.observe(headerAndFiltersRef.current);
        }

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', updateMapTop);
            window.removeEventListener('orientationchange', updateMapTop);
            ro.disconnect();
        };
    }, [showMapOnMobile]);

    const hasClientSideFilters = useMemo(() => {
        return selectedTerms.length > 0 ||
            selectedUnitType ||
            maxPrice ||
            parkingRequired ||
            petsAllowed ||
            gymRequired ||
            poolRequired ||
            laundryRequired ||
            genderPreference;
    }, [selectedTerms, selectedUnitType, maxPrice, parkingRequired, petsAllowed, gymRequired, poolRequired, laundryRequired, genderPreference]);

    const totalListingsForPagination = useMemo(() => {
        if (hasClientSideFilters) {
            return filteredListings.length;
        }
        return totalCount ?? filteredListings.length;
    }, [hasClientSideFilters, filteredListings.length, totalCount]);

    // Calculate total pages based on the total listings count (server-backed when available)
    const totalPages = useMemo(() => {
        return totalListingsForPagination > 0
            ? Math.ceil(totalListingsForPagination / PAGE_SIZE)
            : 0;
    }, [totalListingsForPagination, PAGE_SIZE]);

    /** Search results + map-browse listings for markers / popups only. */
    const mapListings = useMemo(() => {
        if (mapBrowseListings.length === 0) return filteredListings;
        const byId = new Map<string, Listing>();
        filteredListings.forEach((listing) => byId.set(listing.id, listing));
        mapBrowseListings.forEach((listing) => {
            if (!byId.has(listing.id)) byId.set(listing.id, listing);
        });
        return Array.from(byId.values());
    }, [filteredListings, mapBrowseListings]);

    const mobileSelectedListings = useMemo(() => {
        if (mobileMapSelectionIds.length === 0) return [];
        // Map markers can include browse-only listings not in the sidebar list.
        const listingMap = new Map(mapListings.map(listing => [listing.id, listing]));
        return mobileMapSelectionIds
            .map((id) => listingMap.get(id))
            .filter((listing): listing is Listing => Boolean(listing));
    }, [mobileMapSelectionIds, mapListings]);
    const showPagination = totalPages > 1 || hasMore;

    // Only allow going to next page if:
    // 1. We're not on the last page of currently loaded listings, OR
    // 2. There are more items to fetch from the backend (hasMore is true)
    const canGoNext = useMemo(() => {
        // If we're on a page before the last calculated page, we can go next
        if (currentPage < totalPages) {
            return true;
        }
        // If we're on the last page, only allow next if there are more items to fetch from backend
        // This will trigger fetchMoreListings which will add more items to filteredListings
        return currentPage === totalPages && hasMore && !!nextToken;
    }, [currentPage, totalPages, hasMore, nextToken]);

    const canGoPrev = currentPage > 1;

    // Check if a search was initiated (used for fetching, separate from view display)
    const searchInitiated = useMemo(() => {
        return !!selectedUniversity || searchTriggered;
    }, [selectedUniversity, searchTriggered]);

    // Check if we should show the results view (map/panel)
    // This is ONLY true after we've verified there are listings
    const hasActiveSearch = useMemo(() => {
        // If we have a university slug in the URL, we are on a college landing page.
        // We should ALWAYS show the results view (or its loading state) and NEVER the main landing page content.
        if (slug) {
            return true;
        }

        // IMPORTANT: Check URL params directly for immediate detection on SPA navigation
        // This ensures hasActiveSearch is true immediately when navigating to /?university=X
        // before React state (selectedUniversity) has time to update
        if (universityParam) {
            return true;
        }

        // If showing no listings popup, stay on landing page
        if (showNoListingsPopup) {
            return false;
        }
        // Keep results view visible while verifying listings on reloads.
        if (searchInitiated && isCheckingListings) {
            return true;
        }
        // Only show results view if we have verified there are listings
        return searchInitiated && hasVerifiedListings;
    }, [slug, universityParam, searchInitiated, showNoListingsPopup, isCheckingListings, hasVerifiedListings]);

    // City / state searches frame the region only — no location pin (campuses keep their pins).
    const mapFocusLocation = useMemo(() => {
        const selectedId = selectedUniversity ? Number(selectedUniversity) : NaN;
        const selectedState = !Number.isNaN(selectedId)
            ? getStateByUniversityId(selectedId)
            : undefined;
        if (selectedState?.mapCenter) {
            return {
                longitude: selectedState.mapCenter.longitude,
                latitude: selectedState.mapCenter.latitude,
                zoom: selectedState.mapCenter.zoom,
                label: selectedState.label,
            };
        }

        const selectedCity = !Number.isNaN(selectedId)
            ? getCityByUniversityId(selectedId)
            : undefined;
        if (selectedCity?.mapCenter) {
            return {
                longitude: selectedCity.mapCenter.longitude,
                latitude: selectedCity.mapCenter.latitude,
                zoom: selectedCity.mapCenter.zoom,
                label: selectedCity.label,
            };
        }

        if (selectedCity) {
            const campus = getCampusByUniversityId(getCityListingUniversityId(selectedCity));
            if (campus) {
                return {
                    longitude: campus.longitude,
                    latitude: campus.latitude,
                    zoom: 11,
                    label: selectedCity.label,
                };
            }
        }

        const university = universities.find((item) => item.id.toString() === selectedUniversity);
        const campus = !Number.isNaN(selectedId) && selectedId > 0
            ? getCampusByUniversityId(selectedId)
            : undefined;
        if (campus) {
            return {
                longitude: campus.longitude,
                latitude: campus.latitude,
                zoom: 13,
                label: university?.name || campus.name,
                pinLongitude: campus.longitude,
                pinLatitude: campus.latitude,
                iconUrl: getUniversityLogo(campus.name, campus.abbreviation),
            };
        }

        return null;
    }, [selectedUniversity, universities]);

    // Campus pins:
    // - University search: always show that campus.
    // - Location (city/state) search: hidden unless Filters → Show universities.
    const mapCampusPins = useMemo(() => {
        const selectedId = selectedUniversity ? Number(selectedUniversity) : NaN;
        if (Number.isNaN(selectedId)) {
            return [];
        }

        let campusIds: number[] = [];
        if (selectedId > 0) {
            campusIds = [selectedId];
        } else if (showUniversitiesOnMap) {
            const state = getStateByUniversityId(selectedId);
            if (state) {
                campusIds = [...state.universityIds];
            } else {
                const city = getCityByUniversityId(selectedId);
                if (city) {
                    const center = city.mapCenter || { longitude: -82.4572, latitude: 27.9506 };
                    campusIds = getNearestCampuses(center.longitude, center.latitude, {
                        limit: 15,
                        maxMiles: 50,
                        alwaysIncludeIds: [getCityListingUniversityId(city)],
                    }).map((campus) => campus.id);
                }
            }
        }

        return campusIds
            .map((id) => getCampusByUniversityId(id))
            .filter((campus): campus is NonNullable<typeof campus> => !!campus)
            .map((campus) => ({
                id: campus.id,
                label: campus.name,
                abbreviation: campus.abbreviation,
                longitude: campus.longitude,
                latitude: campus.latitude,
                iconUrl: getUniversityLogo(campus.name, campus.abbreviation) || undefined,
            }));
    }, [selectedUniversity, showUniversitiesOnMap]);

    // New location search starts with campus pins off.
    useEffect(() => {
        setShowUniversitiesOnMap(false);
    }, [selectedUniversity]);

    // Fetch featured/recent listings for the landing page when no active search is shown
    useEffect(() => {
        if (hasActiveSearch || landingFeaturedListings.length > 0) {
            return;
        }

        let cancelled = false;

        const fetchLandingFeaturedListings = async () => {
            try {
                setLoadingLandingFeatured(true);
                const url = new URL(API_ENDPOINTS.SUBLEASES.LIST);
                url.searchParams.append('limit', '40');
                const response = await fetch(url.toString());
                if (response.ok) {
                    const data = await response.json();
                    const newListings = (data.data || []).map((listing: any) => ({
                        ...listing,
                        university_id: typeof listing.university_id === 'string' ? parseInt(listing.university_id) : listing.university_id
                    }));
                    
                    const activeListings = newListings.filter((l: any) => l.verification_status !== 'pending');

                    const byNewest = (a: Listing, b: Listing) => {
                        const dateA = new Date(a.created_at || 0).getTime();
                        const dateB = new Date(b.created_at || 0).getTime();
                        return dateB - dateA;
                    };

                    // Boosted first (newest among boosted), then most recent non-boosted
                    const boosted = activeListings
                        .filter((l: Listing) => isListingFeatured(l))
                        .sort(byNewest);
                    const nonBoosted = activeListings
                        .filter((l: Listing) => !isListingFeatured(l))
                        .sort(byNewest);

                    const combined = [...boosted, ...nonBoosted].slice(0, 3);
                    if (!cancelled) setLandingFeaturedListings(combined);
                }
            } catch (error) {
                console.error('Error fetching landing page featured listings:', error);
            } finally {
                if (!cancelled) setLoadingLandingFeatured(false);
            }
        };

        fetchLandingFeaturedListings();
        return () => {
            cancelled = true;
        };
    }, [hasActiveSearch, landingFeaturedListings.length]);

    // Prevent main page scrolling when results/map view is active
    // This combined with 'height: 100vh' on browse-page and overflow-y: auto on list panel
    // ensures only the listings scroll and not the whole page
    // IMPORTANT: Apply no-scroll IMMEDIATELY when URL indicates we should be in search/map view
    // This prevents layout flash before React state is fully initialized
    // Include slug AND searchParams in deps so this runs on SPA navigation (both /college/slug and ?university=X)

    // DISABLED: No longer using no-scroll approach - filters are now below navbar in normal page flow
    /*
    useLayoutEffect(() => {
        // Check URL directly for immediate application (before React state settles)
        const isCollegePage = window.location.pathname.startsWith('/college/');
        const hasUniversityParam = !!universityParam;
        const shouldHaveNoScroll = (isCollegePage || hasUniversityParam) && window.innerWidth > 768;

        if (shouldHaveNoScroll) {
            document.documentElement.classList.add('no-scroll');
            document.body.classList.add('no-scroll');
            window.scrollTo(0, 0);
        }
        // Note: Don't remove class here - let the second useLayoutEffect handle removal
        // to avoid conflicts between the two effects
    }, [slug, universityParam]); // Run on mount AND when slug or university param changes (SPA navigation)

    // Then keep it in sync with React state for dynamic changes
    // Use window.innerWidth directly (not isMobile state) to ensure no-scroll is applied correctly on first paint
    useLayoutEffect(() => {
        const isDesktopViewport = typeof window !== 'undefined' && window.innerWidth > 768;
        if (hasActiveSearch && isDesktopViewport) {
            document.documentElement.classList.add('no-scroll');
            document.body.classList.add('no-scroll');
        } else {
            document.documentElement.classList.remove('no-scroll');
            document.body.classList.remove('no-scroll');
        }

        return () => {
            document.documentElement.classList.remove('no-scroll');
            document.body.classList.remove('no-scroll');
        };
    }, [hasActiveSearch, isMobile]); // isMobile in deps to re-run on resize, but condition uses current window.innerWidth
    */

    // Scroll to top when entering search/map view (separate effect so it doesn't block paint)
    useEffect(() => {
        const isDesktopViewport = typeof window !== 'undefined' && window.innerWidth > 768;
        if (hasActiveSearch && isDesktopViewport) {
            window.scrollTo(0, 0);
        }
    }, [hasActiveSearch, isMobile]);

    // Re-sync viewport when map/results view becomes active so desktop navbar always shows (e.g. after client-side nav).
    useLayoutEffect(() => {
        if (hasActiveSearch) syncViewport();
    }, [hasActiveSearch, syncViewport]);

    // When URL gains university/slug (SPA nav to search), sync viewport immediately so isMobile/isWideDesktop match current window (fixes scale/resize issues).
    useLayoutEffect(() => {
        if (universityParam || slug) syncViewport();
    }, [universityParam, slug, syncViewport]);

    // Fit landing headline: [][T][T][] on desktop, [][T][T][T][T][T][T][] on smaller screens.
    useLayoutEffect(() => {
        if (hasActiveSearch) return;

        const headline = landingHeadlineRef.current;
        if (!headline) return;

        const fitHeadline = () => {
            const lines = headline.querySelectorAll<HTMLElement>('.home-headline-line');
            if (!lines.length) return;

            const card = headline.closest('.home-landing-page') as HTMLElement | null;
            const cardWidth = card?.clientWidth || window.innerWidth;
            const isCompact = window.innerWidth <= 768;
            const ratio = isCompact ? 6 / 7 : 0.5;
            const targetWidth = Math.max(isCompact ? 160 : 140, cardWidth * ratio);
            headline.style.width = `${targetWidth}px`;
            headline.style.maxWidth = `${targetWidth}px`;

            const preferred = Math.min(
                isCompact ? 30 : 34,
                Math.max(isCompact ? 14 : 14, targetWidth * (isCompact ? 0.082 : 0.07))
            );
            const minSize = 10;
            let size = preferred;

            headline.style.fontSize = `${size}px`;
            while (size > minSize) {
                const overflows = Array.from(lines).some((line) => line.scrollWidth > targetWidth + 1);
                if (!overflows) break;
                size -= 0.5;
                headline.style.fontSize = `${size}px`;
            }
        };

        fitHeadline();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fitHeadline) : null;
        const card = headline.closest('.home-landing-page');
        if (card && ro) ro.observe(card);
        else if (ro) ro.observe(document.documentElement);
        window.addEventListener('resize', fitHeadline);
        if (document.fonts?.ready) {
            document.fonts.ready.then(fitHeadline).catch(() => {});
        }

        return () => {
            ro?.disconnect();
            window.removeEventListener('resize', fitHeadline);
        };
    }, [hasActiveSearch, isMobile]);

    const selectedUniversityDisplayName = useMemo(() => {
        if (!selectedUniversity) return 'All Universities';
        const id = Number(selectedUniversity);
        if (Number.isNaN(id)) return 'Selected University';

        const state = getStateByUniversityId(id);
        if (state) return state.label;

        const city = getCityByUniversityId(id);
        if (city) return city.label;

        const uni = universities.find((u) => u.id === id);
        if (uni?.name) return uni.name;

        // Fallback if universities haven't loaded yet
        const fromListings =
            filteredListings.find((l) => Number(l.university_id) === id)?.university_name ||
            filteredListings.find((l) => Number(l.university_id) === id)?.university_abbreviation;
        return fromListings || 'Selected University';
    }, [filteredListings, selectedUniversity, universities]);

    const universityCapsuleButtonRef = useRef<HTMLButtonElement | null>(null);
    const universityCapsuleTextRef = useRef<HTMLSpanElement | null>(null);
    const capsuleMeasureCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const [universityCapsuleFontSize, setUniversityCapsuleFontSize] = useState<number | null>(null);
    const [capsuleFontReadyTick, setCapsuleFontReadyTick] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const fonts = (document as unknown as { fonts?: FontFaceSet }).fonts;
        if (!fonts?.ready) return;

        fonts.ready
            .then(() => {
                if (!cancelled) setCapsuleFontReadyTick((t) => t + 1);
            })
            .catch(() => {
                // ignore
            });

        return () => {
            cancelled = true;
        };
    }, []);

    // Dynamically shrink university name font-size to fit capsule
    const updateUniversityCapsuleFontSize = useCallback(() => {
        if (!selectedUniversity) {
            setUniversityCapsuleFontSize((prev) => (prev === null ? prev : null));
            return;
        }

        const buttonEl = universityCapsuleButtonRef.current;
        const textEl = universityCapsuleTextRef.current;
        if (!buttonEl || !textEl) return;

        const text = selectedUniversityDisplayName || '';
        if (!text) {
            setUniversityCapsuleFontSize((prev) => (prev === null ? prev : null));
            return;
        }

        // Reset to base so measurements are consistent.
        textEl.style.fontSize = '';

        const computed = window.getComputedStyle(buttonEl);
        const padLeft = parseFloat(computed.paddingLeft || '0') || 0;
        const padRight = parseFloat(computed.paddingRight || '0') || 0;
        const availableWidth = Math.max(0, buttonEl.clientWidth - padLeft - padRight);

        const textStyle = window.getComputedStyle(textEl);
        // Prefer sizing based on capsule height so the text "fills" the pill,
        // then shrink only if it overflows horizontally.
        const capsuleHeight = Math.max(0, buttonEl.clientHeight);
        const maxFontSizeByHeight = Math.floor(capsuleHeight * 0.42); // tuned for DM Mono + pill padding
        const maxFontSize = Math.max(12, Math.min(20, maxFontSizeByHeight || 16));

        const fontStyle = textStyle.fontStyle || 'normal';
        const fontWeight = textStyle.fontWeight || '400';
        const fontFamily = textStyle.fontFamily || '"DM Mono", monospace';
        const letterSpacing = textStyle.letterSpacing === 'normal'
            ? 0
            : parseFloat(textStyle.letterSpacing || '0') || 0;

        const canvas = capsuleMeasureCanvasRef.current ?? document.createElement('canvas');
        capsuleMeasureCanvasRef.current = canvas;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            setUniversityCapsuleFontSize((prev) => (prev === null ? prev : null));
            return;
        }

        if (!availableWidth) {
            setUniversityCapsuleFontSize((prev) => (prev === null ? prev : null));
            return;
        }

        const safeWidth = Math.max(0, availableWidth - 10); // leave a little breathing room
        if (!safeWidth) {
            setUniversityCapsuleFontSize((prev) => (prev === null ? prev : null));
            return;
        }

        const measure = (sizePx: number) => {
            ctx.font = `${fontStyle} ${fontWeight} ${sizePx}px ${fontFamily}`;
            return ctx.measureText(text).width + Math.max(0, text.length - 1) * letterSpacing;
        };

        const widthAtMax = measure(maxFontSize);
        if (!widthAtMax) {
            setUniversityCapsuleFontSize((prev) => (prev === null ? prev : null));
            return;
        }

        // Fits at our ideal size: use it.
        if (widthAtMax <= safeWidth) {
            textEl.style.fontSize = `${maxFontSize}px`;
            setUniversityCapsuleFontSize(maxFontSize);
            return;
        }

        // Otherwise, always shrink until it fits (as much as possible).
        const minFontSize = 5;
        let lo = minFontSize;
        let hi = maxFontSize;
        let best = minFontSize;

        while (lo <= hi) {
            const mid = Math.floor((lo + hi) / 2);
            const w = measure(mid);
            if (w && w <= safeWidth) {
                best = mid;
                lo = mid + 1;
            } else {
                hi = mid - 1;
            }
        }

        textEl.style.fontSize = `${best}px`;
        setUniversityCapsuleFontSize(best);
    }, [selectedUniversity, selectedUniversityDisplayName]);

    useLayoutEffect(() => {
        updateUniversityCapsuleFontSize();
    }, [updateUniversityCapsuleFontSize, capsuleFontReadyTick]);

    useEffect(() => {
        if (!selectedUniversity) return;

        const handle = () => updateUniversityCapsuleFontSize();
        const timeouts = [
            window.setTimeout(handle, 0),
            window.setTimeout(handle, 120),
            window.setTimeout(handle, 320),
            window.setTimeout(handle, 700),
        ];

        window.addEventListener('resize', handle);

        let ro: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined' && universityCapsuleButtonRef.current) {
            ro = new ResizeObserver(() => handle());
            ro.observe(universityCapsuleButtonRef.current);
        }

        const fonts = (document as unknown as { fonts?: FontFaceSet }).fonts;
        if (fonts?.addEventListener) {
            fonts.addEventListener('loadingdone', handle);
            fonts.addEventListener('loadingerror', handle);
        }

        return () => {
            timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
            window.removeEventListener('resize', handle);
            ro?.disconnect();
            if (fonts?.removeEventListener) {
                fonts.removeEventListener('loadingdone', handle);
                fonts.removeEventListener('loadingerror', handle);
            }
        };
    }, [selectedUniversity, selectedUniversityDisplayName, updateUniversityCapsuleFontSize]);

    const clearSearchAndReturnHome = useCallback(() => {
        setShowFiltersDropdown(false);
        setSelectedUniversity('');
        setSelectedTerms([]);
        setPendingTerms([]);
        setPriceSort('');
        setSelectedUnitType('');
        setParkingRequired(false);
        setPetsAllowed(false);
        setGymRequired(false);
        setPoolRequired(false);
        setLaundryRequired(false);
        setGenderPreference('');
        setSelectedStartDate('');
        setSelectedEndDate('');
        setPendingStartDate('');
        setPendingEndDate('');
        setSearchTriggered(false);
        setPendingUniversity('');
        setMapBounds(null);
        setMapBrowseListings([]);
        mapBrowseFetchedKeysRef.current = new Set();
        mapBrowseFetchGenRef.current += 1;
        setShowMapOnMobile(false);
        lastNoListingsSearchRef.current = null;

        try {
            localStorage.removeItem('usublease_filters');
        } catch {
            // ignore
        }

        setSearchParams(new URLSearchParams(), { replace: true });
        scrollToTop();
    }, [scrollToTop, setSearchParams]);

    const normalizeUniversityText = useCallback((value: string) => {
        return value.trim().toLowerCase().replace(/\s+/g, ' ');
    }, []);

    const resolveUniversityIdFromInput = useCallback((input: string): string => {
        const raw = input.trim();
        if (!raw) return '';

        // Allow direct ID entry if it matches a supported university
        if (/^\d+$/.test(raw)) {
            const idNum = parseInt(raw);
            return SUPPORTED_UNIVERSITY_IDS.includes(idNum) && universities.some((u) => u.id.toString() === raw) ? raw : '';
        }

        const normalized = normalizeUniversityText(raw);

        // Map city aliases to their city-search IDs.
        const matchedCity = matchSupportedCityFromInput(raw);
        if (matchedCity) {
            return matchedCity.universityId.toString();
        }

        const match = universities.find((u) => {
            const name = normalizeUniversityText(u.name);
            const abbrev = normalizeUniversityText(u.abbreviation || '');
            const matches = name === normalized || (abbrev && abbrev === normalized);
            // Only return match if it's a supported university
            return matches && SUPPORTED_UNIVERSITY_IDS.includes(u.id);
        });
        return match ? match.id.toString() : '';
    }, [normalizeUniversityText, universities]);

    const handleLandingUniversityInputChange = useCallback((value: string) => {
        setPendingUniversityInput(value);

        // If the user edits the text away from the currently-selected pending university,
        // clear the pending university ID so Search uses what's actually in the bar.
        if (!pendingUniversity) return;
        const normalizedInput = normalizeUniversityText(value);
        if (!normalizedInput) return;

        // If the input matches a city alias for the currently selected university, do not clear it
        const pendingCity = getCityByUniversityId(Number(pendingUniversity));
        if (pendingCity) {
            const matched = matchSupportedCityFromInput(value);
            if (matched && matched.universityId === pendingCity.universityId) {
                return;
            }
        }

        const pendingUni = universities.find((u) => u.id.toString() === pendingUniversity);
        if (!pendingUni) {
            setPendingUniversity('');
            return;
        }

        const normalizedName = normalizeUniversityText(pendingUni.name);
        const normalizedAbbrev = normalizeUniversityText(pendingUni.abbreviation || '');

        if (normalizedInput !== normalizedName && (!normalizedAbbrev || normalizedInput !== normalizedAbbrev)) {
            setPendingUniversity('');
        }
    }, [normalizeUniversityText, pendingUniversity, universities]);

    const handleTermClick = useCallback((term: string, isPending: boolean) => {
        const currentTerms = isPending ? pendingTerms : selectedTerms;
        const setTerms = isPending ? setPendingTerms : setSelectedTerms;

        let newTerms;
        if (currentTerms.includes(term)) {
            newTerms = currentTerms.filter(t => t !== term);
        } else {
            newTerms = [...currentTerms, term];
        }

        setTerms(newTerms);

        // When switching TO semesters or adding semesters, clear specific dates
        if (newTerms.length > 0) {
            if (isPending) {
                setPendingStartDate('');
                setPendingEndDate('');
            } else {
                setSelectedStartDate('');
                setSelectedEndDate('');
            }
        }
    }, [pendingTerms, selectedTerms, setPendingTerms, setSelectedTerms, setPendingStartDate, setPendingEndDate, setSelectedStartDate, setSelectedEndDate]);

    const triggerLandingSearch = useCallback(() => {
        const universityIdFromInput = resolveUniversityIdFromInput(pendingUniversityInput);

        // Check if user has typed something but hasn't selected a valid university
        // Recognized city aliases may be searched directly without selecting a dropdown item.
        if (pendingUniversityInput.trim() && !isUniversityInputValid && !universityIdFromInput) {
            // Show validation error
            setShowValidationError(true);
            // Auto-hide after 4 seconds
            setTimeout(() => {
                setShowValidationError(false);
            }, 4000);
            return { didTrigger: false as const, universityIdToSearch: '' };
        }

        // Prefer the ID if it's in sync with the text; otherwise resolve from typed input.
        const universityIdToSearch = pendingUniversity || universityIdFromInput;

        // Allow search with no criteria - this will search all universities
        // No need to check for hasCriteria anymore

        // Set checking state first to prevent view transition (use ref for sync)
        isCheckingListingsRef.current = true;
        setIsCheckingListings(true);
        setHasVerifiedListings(false);
        setShowNoListingsPopup(false);
        setIsNoListingsPopupFadingOut(false);
        setIsUnsupportedUniversity(false);
        
        // Clear the no-listings lock for the new search to allow retries if needed
        lastNoListingsSearchRef.current = null;
        setRetryCount(0);


        // Ensure the landing search component has an ID if the typed input was a valid university
        if (!pendingUniversity && universityIdFromInput) {
            setPendingUniversity(universityIdFromInput);
        }

        if (universityIdToSearch) {
            setSelectedUniversity(universityIdToSearch);
        } else {
            // Clear university to search all universities
            setSelectedUniversity('');
        }

        // Apply pending terms and dates
        setSelectedTerms(pendingTerms);
        setSelectedStartDate(pendingStartDate);
        setSelectedEndDate(pendingEndDate);

        setSearchTriggered(true);
        setSearchRequestKey((k) => k + 1);

        // Always default to list view when starting a new search from landing
        setShowMapOnMobile(false);

        return { didTrigger: true as const, universityIdToSearch };
    }, [pendingUniversity, pendingUniversityInput, resolveUniversityIdFromInput, pendingTerms, isUniversityInputValid, pendingStartDate, pendingEndDate]);


    // Auto-hide the "No listings" popup after 6 seconds with fade-out
    useEffect(() => {
        if (showNoListingsPopup) {
            // Start fade-out animation after 5.7 seconds
            const fadeOutTimer = setTimeout(() => {
                setIsNoListingsPopupFadingOut(true);
            }, 5700);

            // Hide popup completely after 6 seconds (300ms after fade starts)
            const hideTimer = setTimeout(() => {
                setShowNoListingsPopup(false);
                setIsNoListingsPopupFadingOut(false);
                setIsUnsupportedUniversity(false);
                setSearchTriggered(false);
                setSelectedUniversity('');
                setIsCheckingListings(false);
                setHasVerifiedListings(false);
                isCheckingListingsRef.current = false;
            }, 6000);

            return () => {
                clearTimeout(fadeOutTimer);
                clearTimeout(hideTimer);
            };
        }
    }, [showNoListingsPopup]);

    // Auto-hide the vote thanks capsule after a few seconds
    useEffect(() => {
        if (showVoteThanksPopup) {
            const fadeOutTimer = setTimeout(() => {
                setIsVoteThanksFadingOut(true);
            }, 2700);

            const hideTimer = setTimeout(() => {
                setShowVoteThanksPopup(false);
                setIsVoteThanksFadingOut(false);
            }, 3000);

            return () => {
                clearTimeout(fadeOutTimer);
                clearTimeout(hideTimer);
            };
        }
    }, [showVoteThanksPopup]);

    // Clear the no-listings lock when user changes to a different university
    useEffect(() => {
        if (pendingUniversity && lastNoListingsSearchRef.current && pendingUniversity !== lastNoListingsSearchRef.current) {
            // User is selecting a different university, clear the lock
            lastNoListingsSearchRef.current = null;
        }
    }, [pendingUniversity]);

    // Reset search trigger and pending university when returning to landing page
    useEffect(() => {
        if (!selectedUniversity && selectedTerms.length === 0) {
            setSearchTriggered(false);
            setPendingUniversity(''); // Clear pending university when returning to landing page
        }
    }, [selectedUniversity, selectedTerms.length]);

    // Auto-retry fetching listings when in list/map view with no listings
    // If user got to the list/map view, they have a valid university, so we should retry
    useEffect(() => {
        // Clear any existing retry timeout
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }

        // Only retry if:
        // 1. We're in the active search view (hasActiveSearch is true)
        // 2. There are no displayed listings
        // 3. Not currently loading
        // 4. We have a selected university (valid university)
        // 5. Haven't exceeded max retries (limit to 5 retries)
        const shouldRetry = hasActiveSearch &&
            displayedListings.length === 0 &&
            !loading &&
            !loadingMore &&
            !!selectedUniversity &&
            lastNoListingsSearchRef.current !== selectedUniversity && // Don't retry if we already confirmed 0
            retryCount < 5;


        if (shouldRetry) {
            // Retry after a short delay (2 seconds)
            retryTimeoutRef.current = setTimeout(() => {
                setRetryCount(prev => prev + 1);
                // Trigger a new fetch by incrementing searchRequestKey
                setSearchRequestKey((k) => k + 1);
            }, 2000);
        }

        return () => {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }
        };
    }, [hasActiveSearch, displayedListings.length, loading, loadingMore, selectedUniversity, retryCount]);

    // Reset retry count when listings are found or university changes
    useEffect(() => {
        if (displayedListings.length > 0) {
            setRetryCount(0);
        }
    }, [displayedListings.length]);

    useEffect(() => {
        // Reset retry count when university changes
        setRetryCount(0);
    }, [selectedUniversity]);



    useEffect(() => {
        if (totalPages > 0 && currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
        if (totalPages === 0 && currentPage !== 1) {
            setCurrentPage(1);
        }
    }, [totalPages, currentPage]);

    const getUniversityName = (listing: Listing) => {
        // First try to get from listing's university_name field
        if (listing.university_name) {
            return listing.university_name;
        }
        // Fallback to looking up in universities array
        const university = universities.find(u => u.id === listing.university_id);
        return university ? university.name : 'Unknown';
    };

    const getUniversityAbbreviation = (listing: Listing) => {
        let abbrev = listing.university_abbreviation;
        if (!abbrev) {
            const university = universities.find(u => u.id === listing.university_id);
            abbrev = university?.abbreviation;
        }
        if (!abbrev) {
            abbrev = getUniversityName(listing);
        }

        // City listings use university_id 0 ("General") — show the city instead
        const isGeneral =
            listing.university_id === 0 ||
            abbrev === 'General' ||
            listing.university_name === 'General';

        const formatCityTag = (city: string) => {
            const cityNorm = city.trim().toLowerCase();
            if (
                cityNorm === 'new york city' ||
                cityNorm === 'new york city, ny' ||
                cityNorm === 'new york' ||
                cityNorm === 'nyc'
            ) {
                return 'NYC';
            }
            return city.trim();
        };

        if (isGeneral && listing.city) {
            return formatCityTag(listing.city);
        }

        // City-mapped university names like "New York City, NY"
        if (typeof abbrev === 'string') {
            const abbrevNorm = abbrev.trim().toLowerCase();
            if (
                abbrevNorm === 'new york city' ||
                abbrevNorm === 'new york city, ny' ||
                abbrevNorm.startsWith('new york city')
            ) {
                return 'NYC';
            }
        }

        return abbrev;
    };

    const getOwnerDisplay = (listing: Listing) => {
        // Handle alt-listings and Facebook source listings
        if (listing.is_alt_listing || listing.source === 'facebook') {
            // Show the uploader's name and photo from the Facebook listing
            const firstName = listing.facebook_first_name || '';
            const lastName = listing.facebook_last_name || '';
            const fullName = `${firstName} ${lastName}`.trim() || 'Facebook User';
            return {
                name: fullName,
                firstName: firstName || 'User',
                avatarUrl: listing.facebook_avatar_url || null,
                isFacebook: true,
            };
        }
        // Combine first and last name for regular users
        // Handle undefined, null, or empty string values
        const firstName = (listing.user_first_name ?? '') || '';
        const lastName = (listing.user_last_name ?? '') || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return {
            name: fullName,
            firstName: firstName || 'User',
            avatarUrl: listing.user_avatar_url || null,
            isFacebook: false,
        };
    };

    const formatTerm = (term: string) => {
        const termMap: { [key: string]: string } = {
            'fall': 'Fall',
            'spring': 'Spring',
            'summer': 'Summer'
        };
        return termMap[term] || term;
    };

    const formatPrice = (price: number) => {
        if (!price) return 'N/A';
        return price.toString();
    };

    const formatDate = (listing: Listing) => {
        const { lease_start_date, lease_end_date, lease_term } = listing;
        
        let start = lease_start_date;
        let end = lease_end_date;
        
        // If missing, infer from term (fallback for UI while DB is updating)
        if (!start || !end) {
            const term = (lease_term || '').toLowerCase();
            if (term.includes('spring')) {
                start = start || '2026-01-01';
                end = end || '2026-05-31';
            } else if (term.includes('summer')) {
                start = start || '2026-05-01';
                end = end || '2026-07-31';

            } else if (term.includes('fall')) {
                start = start || '2026-08-01';
                end = end || '2026-12-31';
            }
        }
        
        if (start && end) {
            const s = new Date(start + 'T00:00:00');
            const e = new Date(end + 'T00:00:00');
            if (isMobile) {
                const formatOptions: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
                return `${s.toLocaleDateString('en-US', formatOptions)} - ${e.toLocaleDateString('en-US', formatOptions)}`;
            }
            return `${s.getMonth() + 1}/${s.getDate()} - ${e.getMonth() + 1}/${e.getDate()}`;
        }
        
        // Final fallback to created_at if absolutely no lease info
        if (!listing.created_at) return '';
        const date = new Date(listing.created_at);
        return `Posted: ${date.getMonth() + 1}/${date.getDate()}`;
    };

    const renderListingCard = (
        listing: Listing,
        index: number,
        attachRef = true,
        variant: 'default' | 'mapPreview' | 'featuredLanding' = 'default'
    ) => {
        // Get compressed image URLs - prefer compressed_pictures for homescreen
        // Alt-listings and Facebook: always use pictures directly; backend "compressed" URLs
        // (e.g. _thumb suffix) often don't exist for external CDN URLs, so they fail to load.
        let compressedImageUrls: string[] = [];
        if (listing.is_alt_listing || listing.source === 'facebook') {
            compressedImageUrls = listing.pictures && Array.isArray(listing.pictures) ? listing.pictures : [];
        } else if (listing.compressed_pictures &&
            Array.isArray(listing.compressed_pictures) &&
            listing.compressed_pictures.length > 0) {
            const validCompressed = listing.compressed_pictures.filter(url => url && url.trim() !== '');
            if (validCompressed.length > 0) {
                compressedImageUrls = validCompressed;
            }
        }

        // Fallback to thumbnails if no compressed images (non-alt only; alt already handled above)
        if (compressedImageUrls.length === 0 && !listing.is_alt_listing && listing.source !== 'facebook') {
            compressedImageUrls = (listing.pictures && Array.isArray(listing.pictures) && listing.pictures.length > 0)
                ? listing.pictures.map(url => getThumbnailUrl(url))
                : [];
        }

        // Get HD image URLs (original pictures)
        let hdImageUrls: string[] = [];
        if (listing.pictures && Array.isArray(listing.pictures) && listing.pictures.length > 0) {
            // Alt-listings and Facebook listings use pictures directly
            if (listing.is_alt_listing || listing.source === 'facebook') {
                hdImageUrls = listing.pictures;
            } else {
                // For non-facebook, use original pictures (not thumbnails)
                hdImageUrls = listing.pictures;
            }
        }

        // Use compressed images as the primary display
        const imageUrls = compressedImageUrls;
        const hasImages = imageUrls.length > 0;
        const hasCoordinates = typeof listing.latitude === 'number' && typeof listing.longitude === 'number';

        if (variant === 'featuredLanding') {
            const primaryImageUrl = imageUrls[0] || (listing.pictures && listing.pictures[0]) || '';
            const ownerDisplay = getOwnerDisplay(listing);
            const ownerName = ownerDisplay.name.trim();
            const universityName = getUniversityAbbreviation(listing);

            const isBoosted = isListingFeatured(listing);

            return (
                <div
                    key={listing.id}
                    data-listing-id={listing.id}
                    className={`listing-card--featured-landing${isBoosted ? ' listing-boosted' : ''}`}
                    onClick={() => navigate(`/listing/${listing.id}`)}
                >
                    <div className="listing-card--featured-landing__media">
                        <ImageWithFallback
                            src={primaryImageUrl}
                            fallbackSrc={listing.pictures && listing.pictures[0]}
                            alt={listing.title}
                            className="listing-card--featured-landing__img"
                            loading="lazy"
                        />
                    </div>
                    <div className="listing-card--featured-landing__body">
                        <div className="listing-card--featured-landing__title-row">
                            <h3 className="listing-card--featured-landing__title">{listing.title}</h3>
                            {universityName && (
                                <div className="listing-card--featured-landing__university">{universityName}</div>
                            )}
                        </div>
                        
                        {(ownerName || ownerDisplay.avatarUrl) && (
                            <div className="listing-card--featured-landing__owner">
                                {ownerDisplay.avatarUrl ? (
                                    <img
                                        className="listing-card--featured-landing__avatar"
                                        src={ownerDisplay.avatarUrl}
                                        alt={ownerName}
                                    />
                                ) : (
                                    <div className="listing-card--featured-landing__avatar-placeholder">
                                        {ownerName ? ownerName.charAt(0).toUpperCase() : '?'}
                                    </div>
                                )}
                                <span className="listing-card--featured-landing__name">{ownerName || 'User'}</span>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        if (variant === 'mapPreview') {
            const previewImageUrl = imageUrls[0] || '';
            const mapPreviewOwnerName = getOwnerDisplay(listing).name.trim();
            return (
                <div
                    key={listing.id}
                    data-listing-id={listing.id}
                    className="listing-card listing-card--map-preview"
                    onClick={() => {
                        const url = getListingUrl(listing.id);
                        // Desktop map screen: open listing in a new tab
                        if (!isMobile && showMapOnMobile) {
                            window.open(url, '_blank', 'noopener,noreferrer');
                            return;
                        }
                        navigate(url);
                    }}
                >
                    <div className="listing-card--map-preview__media" aria-hidden="true">
                        {previewImageUrl ? (
                            <img
                                className="listing-card--map-preview__img"
                                src={previewImageUrl}
                                alt=""
                                loading="eager"
                                decoding="sync"
                            />
                        ) : (
                            <div className="listing-card--map-preview__img-skeleton" />
                        )}
                        {isListingFeatured(listing) && (
                            <span className="listing-promoted-badge">Featured</span>
                        )}
                    </div>
                    <div className="listing-card--map-preview__body">
                        <div className="listing-card--map-preview__top">
                            <div className="listing-card--map-preview__title">{listing.title}</div>
                            <div className="listing-card--map-preview__price">${formatPrice(listing.monthly_rent)}</div>
                        </div>
                        <div className="listing-card--map-preview__subtitle">{getUniversityAbbreviation(listing)}</div>
                        <div className="listing-card--map-preview__meta">
                            <div className="listing-card--map-preview__tags">
                                {mapPreviewOwnerName && (
                                    <span className="feature-tag">{mapPreviewOwnerName}</span>
                                )}
                                {listing.gender_preference === 'male_preferred' && (
                                    <span className="feature-tag gender-male">Males</span>
                                )}
                                {listing.gender_preference === 'female_preferred' && (
                                    <span className="feature-tag gender-female">Females</span>
                                )}
                                <span className="feature-tag">{listing.unit_type}</span>
                                {!hasCoordinates && <span className="feature-tag location-pending">Location pending</span>}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div
                key={listing.id}
                ref={attachRef ? (el) => { listingRefs.current[listing.id] = el; } : undefined}
                data-listing-id={listing.id}
                className={`listing-card listing-card-visible ${selectedListingId === listing.id && !selectedFromMap ? 'listing-card-highlight' : ''}`}
                onClick={() => {
                    setSelectedFromMap(false);
                    navigateToListing(listing.id);
                }}
            >
                <div className="listing-image">
                    {isListingFeatured(listing) && (
                        <span className="listing-promoted-badge">Featured</span>
                    )}
                    {imageUrls.length > 0 ? (
                        <ImageCarousel
                            images={imageUrls}
                            hdImages={allCompressedLoaded && hdImageUrls.length > 0 ? hdImageUrls : undefined}
                            alt={listing.title}
                            onImageClick={() => navigate(`/listing/${listing.id}`)}
                            fetchPriority={index < 4 ? 'high' : 'auto'}
                            loadOnlyPrimary={false}
                            loadFirstTwo={false}
                            fallbackImages={listing.pictures && Array.isArray(listing.pictures) && listing.pictures.length > 0
                                ? ((listing.is_alt_listing || listing.source === 'facebook')
                                    ? listing.pictures
                                    : listing.pictures.map(url => getThumbnailUrl(url)))
                                : []}
                        />
                    ) : (
                        <div className="image-skeleton">
                            <div className="skeleton-shimmer"></div>
                        </div>
                    )}

                    <div className="listing-price-badge">
                        ${formatPrice(listing.monthly_rent)}
                    </div>

                </div>

                <div className="listing-content">
                    <div className="listing-info">
                        <h3 className="browse-listing-title">{listing.title}</h3>
                        <div className="listing-features">
                            {/* Then gender */}
                            {listing.gender_preference === 'male_preferred' && <span className="feature-tag gender-male">Males</span>}
                            {listing.gender_preference === 'female_preferred' && <span className="feature-tag gender-female">Females</span>}
                            {/* Then unit type */}
                            <span className="feature-tag">{listing.unit_type}</span>
                            {/* Then the rest of the tags */}
                            {(listing.parking === true || String(listing.parking).toLowerCase() === "true") && <span className="feature-tag">Parking</span>}
                            {(listing.allow_pets === true || String(listing.allow_pets).toLowerCase() === "true") && <span className="feature-tag">Pet Friendly</span>}
                            {(listing.gym === true || String(listing.gym).toLowerCase() === "true") && <span className="feature-tag">Gym</span>}
                            {(listing.pool === true || String(listing.pool).toLowerCase() === "true") && <span className="feature-tag">Pool</span>}
                            {(listing.laundry === true || String(listing.laundry).toLowerCase() === "true") && <span className="feature-tag">Laundry</span>}
                            {!hasCoordinates && <span className="feature-tag location-pending">Location pending</span>}
                        </div>
                        <div className="listing-card-footer">
                            {(() => {
                                const ownerDisplay = getOwnerDisplay(listing);
                                const hasUser = listing.user_id || listing.source === 'facebook';
                                const hasName = ownerDisplay.name && ownerDisplay.name.trim() !== '';
                                const hasAvatar = !!ownerDisplay.avatarUrl;

                                // Show profile section if there's a user, name, or avatar
                                if (!hasUser && !hasName && !hasAvatar) {
                                    return null;
                                }

                                return (
                                    <div className="listing-user-info">
                                        <div className="user-avatar-small">
                                            {ownerDisplay.avatarUrl ? (
                                                <img
                                                    className="user-avatar-img-small"
                                                    src={ownerDisplay.avatarUrl}
                                                    alt={ownerDisplay.name}
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = 'none';
                                                        const parent = target.parentElement;
                                                        if (parent) {
                                                            const placeholder = parent.querySelector('.user-avatar-placeholder-small');
                                                            if (placeholder) {
                                                                (placeholder as HTMLElement).style.display = 'flex';
                                                            }
                                                        }
                                                    }}
                                                />
                                            ) : null}
                                            <div
                                                className="user-avatar-placeholder-small"
                                                style={{
                                                    display: ownerDisplay.avatarUrl ? 'none' : 'flex',
                                                    backgroundColor: 'transparent',
                                                    color: '#81C784',
                                                    width: '100%',
                                                    height: '100%',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'

                                                }}
                                            >
                                                <svg
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <rect width="24" height="24" fill="#E8F5E9" />
                                                    <circle cx="12" cy="9" r="3.5" fill="#81C784" />
                                                    <path
                                                        d="M6 20c0-3.314 2.686-6 6-6s6 2.686 6 6"
                                                        fill="#81C784"
                                                    />
                                                </svg>
                                            </div>
                                            {listing.user_student_verification_status === 'approved' && (
                                                <div
                                                    className="listing-card-avatar-verified-badge"
                                                    title="Verified User"
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M8.5 12.5L11 15L16 9.5" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                        {hasName && (
                                            <span
                                                className="user-name-small"
                                                title={ownerDisplay.name}
                                            >
                                                {ownerDisplay.firstName}
                                                {listing.source === 'facebook' && (
                                                    <span className="user-name-badge"></span>
                                                )}
                                            </span>
                                        )}
                                    </div>
                                );
                            })()}
                            <div className="listing-date-capsule-wrapper">
                                <span className="listing-date-capsule">{formatDate(listing)}</span>
                            </div>
                            {(() => {
                                const university = universities.find(u => u.id === listing.university_id);
                                if (!university) return null;
                                const logoUrl = getUniversityLogo(university.name, university.abbreviation);
                                if (!logoUrl) return null;
                                return (
                                    <div className="listing-university-capsule-wrapper">
                                        <span className="listing-university-capsule">
                                            <img src={logoUrl} alt={university.abbreviation} className="university-icon-small" />
                                        </span>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            </div>
        );
    };


    // Render browse-header-and-filters content
    const renderBrowseHeaderAndFilters = (className: string = '', attachRef: boolean = false) => (
        <div className={`browse-header-and-filters ${className}`} ref={attachRef ? headerAndFiltersRef : undefined}>
            <div className="home-search-filters-container">
                <div className="filter-group university-search-group">
                    <UniversitySearch
                        value={selectedUniversity}
                        onChange={setSelectedUniversity}
                        onUnsupportedSearch={(query) => {
                            setIsUnsupportedUniversity(true);
                            setShowNoListingsPopup(true);
                            setVotePrepopulatedUniversity(query || '');
                        }}
                        placeholder="Search University or City"
                        className="home-page-university-search"
                        supportedUniversityIds={SUPPORTED_UNIVERSITY_IDS}
                        showCities={true}
                    />
                </div>

                {selectedTerms.length === 0 && (
                    <div className="filter-group home-search-date-picker-group">
                        <DateRangePicker
                            className="home-search-date-picker"
                            startDate={selectedStartDate}
                            endDate={selectedEndDate}
                            disabled={selectedTerms.length > 0}
                            onChange={(start, end) => {
                                setSelectedStartDate(start);
                                setSelectedEndDate(end);
                                if (start || end) {
                                    setSelectedTerms([]);
                                }
                            }}
                        />
                    </div>
                )}

                <div className="filter-group icon-button-group">
                    <div className="filters-dropdown-container">
                        <button
                            ref={filterButtonRef}
                            className="filters-button"
                            onClick={() => setShowFiltersDropdown(!showFiltersDropdown)}
                            type="button"
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ display: 'block', flexShrink: 0 }}
                            >
                                <path
                                    d="M22 3H2L10 12.46V19L14 21V12.46L22 3Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    fill="none"
                                    vectorEffect="non-scaling-stroke"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
                {/* Show map toggle button on mobile or smaller desktops */}
                {isCompactLayout && (
                    <div className="filter-group icon-button-group">
                        <button
                            className="filters-button map-toggle-button"
                            onClick={() => {
                                setShowMapOnMobile(prev => !prev);
                            }}
                            type="button"
                            aria-label={showMapOnMobile ? 'Show list view' : 'Show map view'}
                            aria-pressed={showMapOnMobile}
                        >
                            {showMapOnMobile ? (
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{ display: 'block', flexShrink: 0 }}
                                >
                                    <path
                                        d="M4 6H20M4 12H20M4 18H20"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        fill="none"
                                        vectorEffect="non-scaling-stroke"
                                    />
                                </svg>
                            ) : (
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{ display: 'block', flexShrink: 0 }}
                                >
                                    <path
                                        d="M3 6L9 4L15 6L21 4V18L15 20L9 18L3 20V6Z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        fill="none"
                                        vectorEffect="non-scaling-stroke"
                                    />
                                    <path
                                        d="M9 4V18M15 6V20"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        fill="none"
                                        vectorEffect="non-scaling-stroke"
                                    />
                                </svg>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <>
            <NavBar
                inverted={true}
            />
            {/* Class-based padding (--home-nav-offset) so content is never cut off; class set from URL + viewport for first-paint correctness */}
            <div className={(universityParam || slug || hasActiveSearch) && typeof window !== 'undefined' && window.innerWidth > 768 ? 'home-desktop-search-wrapper' : ''}>
                {showFiltersDropdown && !isCompactLayout && (
                    <>
                        <div
                            className="filters-dim-overlay"
                            onClick={() => setShowFiltersDropdown(false)}
                        />
                        <div
                            className="home-desktop-filters-panel"
                            style={filterButtonRect ? {
                                top: `${filterButtonRect.bottom + 8}px`,
                                left: `${filterButtonRect.left + (filterButtonRect.width / 2)}px`,
                            } : undefined}
                        >
                            <div className="filter-section">
                                <label className="filter-section-label">Sort by</label>
                                <div className="sort-buttons">
                                    <button
                                        className={`sort-button ${priceSort === '' ? 'selected' : ''}`}
                                        onClick={() => setPriceSort('')}
                                    >
                                        Recent
                                    </button>
                                    <button
                                        className={`sort-button ${priceSort === 'low-to-high' ? 'selected' : ''}`}
                                        onClick={() => setPriceSort('low-to-high')}
                                    >
                                        Price ↑
                                    </button>
                                    <button
                                        className={`sort-button ${priceSort === 'high-to-low' ? 'selected' : ''}`}
                                        onClick={() => setPriceSort('high-to-low')}
                                    >
                                        Price ↓
                                    </button>
                                </div>
                            </div>


                            <div className="filter-option">
                                <label htmlFor="desktop-panel-unit-type-filter">Unit Type</label>
                                <select
                                    id="desktop-panel-unit-type-filter"
                                    value={selectedUnitType}
                                    onChange={(e) => setSelectedUnitType(e.target.value)}
                                >
                                    <option value="">All</option>
                                    <option value="studio">Studio</option>
                                    <option value="1x1">1x1</option>
                                    <option value="1x2">1x2</option>
                                    <option value="2x1">2x1</option>
                                    <option value="2x2">2x2</option>
                                    <option value="3x1">3x1</option>
                                    <option value="3x2">3x2</option>
                                    <option value="3x3">3x3</option>
                                    <option value="4x2">4x2</option>
                                    <option value="4x3">4x3</option>
                                    <option value="4x4">4x4</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div className="filter-section">
                                <label className="filter-section-label">Gender Preference</label>
                                <div className="filter-buttons-row">
                                    <button
                                        className={`filter-button ${genderPreference === '' ? 'selected' : ''}`}
                                        onClick={() => setGenderPreference('')}
                                    >
                                        All
                                    </button>
                                    <button
                                        className={`filter-button ${genderPreference === 'male_preferred' ? 'selected' : ''}`}
                                        onClick={() => setGenderPreference('male_preferred')}
                                    >
                                        Males
                                    </button>
                                    <button
                                        className={`filter-button ${genderPreference === 'female_preferred' ? 'selected' : ''}`}
                                        onClick={() => setGenderPreference('female_preferred')}
                                    >
                                        Females
                                    </button>
                                </div>
                            </div>

                            <div className="filter-section">
                                <label className="filter-section-label">Tags</label>
                                <div className="filter-buttons-row">
                                    <button
                                        className={`filter-button ${petsAllowed ? 'selected' : ''}`}
                                        onClick={() => setPetsAllowed(!petsAllowed)}
                                    >
                                        Pets
                                    </button>
                                    <button
                                        className={`filter-button ${parkingRequired ? 'selected' : ''}`}
                                        onClick={() => setParkingRequired(!parkingRequired)}
                                    >
                                        Parking
                                    </button>
                                    <button
                                        className={`filter-button ${gymRequired ? 'selected' : ''}`}
                                        onClick={() => setGymRequired(!gymRequired)}
                                    >
                                        Gym
                                    </button>
                                    <button
                                        className={`filter-button ${poolRequired ? 'selected' : ''}`}
                                        onClick={() => setPoolRequired(!poolRequired)}
                                    >
                                        Pool
                                    </button>
                                </div>
                            </div>

                            {Number(selectedUniversity) < 0 && (
                                <div className="filter-section">
                                    <label className="filter-section-label">Map</label>
                                    <div className="filter-buttons-row">
                                        <button
                                            type="button"
                                            className={`filter-button ${showUniversitiesOnMap ? 'selected' : ''}`}
                                            onClick={() => setShowUniversitiesOnMap(!showUniversitiesOnMap)}
                                        >
                                            Show universities
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="filter-section listing-count-section">
                                <div className="listing-count-capsule">
                                    {loading ? <div className="showing-count-skeleton"></div> : `${totalListingsForPagination} Listings`}
                                </div>
                            </div>
                        </div>
                    </>
                )}
                {/* Mobile Modal Overlay - outside browse-page so it darkens navbar/footer too */}
                {showFiltersDropdown && isCompactLayout && (
                    <div className="filters-modal-overlay" onClick={() => setShowFiltersDropdown(false)}>
                        <div className="filters-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="filter-section">
                                <label className="filter-section-label">Sort by</label>
                                <div className="sort-buttons">
                                    <button
                                        className={`sort-button ${priceSort === '' ? 'selected' : ''}`}
                                        onClick={() => setPriceSort('')}
                                    >
                                        Recent
                                    </button>
                                    <button
                                        className={`sort-button ${priceSort === 'low-to-high' ? 'selected' : ''}`}
                                        onClick={() => setPriceSort('low-to-high')}
                                    >
                                        Price ↑
                                    </button>
                                    <button
                                        className={`sort-button ${priceSort === 'high-to-low' ? 'selected' : ''}`}
                                        onClick={() => setPriceSort('high-to-low')}
                                    >
                                        Price ↓
                                    </button>
                                </div>
                            </div>


                            <div className="filter-option">
                                <label htmlFor="modal-unit-type-filter">Unit Type</label>
                                <select
                                    id="modal-unit-type-filter"
                                    value={selectedUnitType}
                                    onChange={(e) => setSelectedUnitType(e.target.value)}
                                >
                                    <option value="">All</option>
                                    <option value="studio">Studio</option>
                                    <option value="1x1">1x1</option>
                                    <option value="1x2">1x2</option>
                                    <option value="2x1">2x1</option>
                                    <option value="2x2">2x2</option>
                                    <option value="3x1">3x1</option>
                                    <option value="3x2">3x2</option>
                                    <option value="3x3">3x3</option>
                                    <option value="4x2">4x2</option>
                                    <option value="4x3">4x3</option>
                                    <option value="4x4">4x4</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div className="filter-section">
                                <label className="filter-section-label">Gender Preference</label>
                                <div className="filter-buttons-row">
                                    <button
                                        className={`filter-button ${genderPreference === '' ? 'selected' : ''}`}
                                        onClick={() => setGenderPreference('')}
                                    >
                                        All
                                    </button>
                                    <button
                                        className={`filter-button ${genderPreference === 'male_preferred' ? 'selected' : ''}`}
                                        onClick={() => setGenderPreference('male_preferred')}
                                    >
                                        Males
                                    </button>
                                    <button
                                        className={`filter-button ${genderPreference === 'female_preferred' ? 'selected' : ''}`}
                                        onClick={() => setGenderPreference('female_preferred')}
                                    >
                                        Females
                                    </button>
                                </div>
                            </div>

                            <div className="filter-section">
                                <label className="filter-section-label">Tags</label>
                                <div className="filter-buttons-row">
                                    <button
                                        className={`filter-button ${petsAllowed ? 'selected' : ''}`}
                                        onClick={() => setPetsAllowed(!petsAllowed)}
                                    >
                                        Pets
                                    </button>
                                    <button
                                        className={`filter-button ${parkingRequired ? 'selected' : ''}`}
                                        onClick={() => setParkingRequired(!parkingRequired)}
                                    >
                                        Parking
                                    </button>
                                    <button
                                        className={`filter-button ${gymRequired ? 'selected' : ''}`}
                                        onClick={() => setGymRequired(!gymRequired)}
                                    >
                                        Gym
                                    </button>
                                    <button
                                        className={`filter-button ${poolRequired ? 'selected' : ''}`}
                                        onClick={() => setPoolRequired(!poolRequired)}
                                    >
                                        Pool
                                    </button>
                                </div>
                            </div>

                            {Number(selectedUniversity) < 0 && (
                                <div className="filter-section">
                                    <label className="filter-section-label">Map</label>
                                    <div className="filter-buttons-row">
                                        <button
                                            type="button"
                                            className={`filter-button ${showUniversitiesOnMap ? 'selected' : ''}`}
                                            onClick={() => setShowUniversitiesOnMap(!showUniversitiesOnMap)}
                                        >
                                            Show universities
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="filter-section listing-count-section">
                                <div className="listing-count-capsule">
                                    {loading ? <div className="showing-count-skeleton"></div> : `${totalListingsForPagination} Listings`}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div className={`browse-page${showMapOnMobile ? ' browse-page--map' : ''}${hasActiveSearch && !effectiveMobile ? ' browse-page--search-active' : ''}`}>
                    {/* Mobile and smaller desktop: render browse-header-and-filters below navbar */}
                    {hasActiveSearch && isCompactLayout && renderBrowseHeaderAndFilters('browse-header-and-filters--mobile', true)}

                    {/* Desktop: render browse-header-and-filters below navbar */}
                    {hasActiveSearch && !isCompactLayout && renderBrowseHeaderAndFilters('browse-header-and-filters--desktop', false)}

                    {hasActiveSearch ? (
                        <div className={`home-results${(isCompactLayout && showMapOnMobile) ? ' home-results--map' : ''}${(!isCompactLayout) ? ' home-results--desktop-split' : ''}`}>
                            {/* Desktop: Always show both list and map side by side */}
                            {!isCompactLayout ? (
                                <>
                                    <div className="home-list-panel" ref={listPanelRef}>
                                        <div ref={listTopRef} />
                                        {/* Listing count removed from here */}
                                        {slug && selectedUniversityDisplayName !== 'All Universities' && (
                                            <h1 style={{ position: 'absolute', width: '1px', height: '1px', padding: '0', margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', border: '0' }}>
                                                {selectedUniversityDisplayName} Student Subleases
                                            </h1>
                                        )}

                                        <div className="listings">
                                            {/* Show skeletons while loading, retrying, or checking for listings */}
                                            {(loading || (loadingMore && displayedListings.length === 0) || isCheckingListings) && displayedListings.length === 0 ? (
                                                <>
                                                    {Array.from({ length: PAGE_SIZE }).map((_, index) => (
                                                        <div key={`skeleton-${index}`} className="listing-card-skeleton">
                                                            <div className="skeleton-image"></div>
                                                            <div className="skeleton-content">
                                                                <div className="skeleton-title"></div>
                                                                <div className="skeleton-subtitle"></div>
                                                                <div className="skeleton-tags">
                                                                    <div className="skeleton-tag"></div>
                                                                    <div className="skeleton-tag"></div>
                                                                    <div className="skeleton-tag"></div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </>
                                            ) : null}
                                            {/* Render listings as soon as they're available */}
                                            {displayedListings.length > 0 ? (
                                                <>
                                                    {displayedListings.map((listing, index) => renderListingCard(listing, index))}
                                                </>
                                            ) : !loading && !isCheckingListings && displayedListings.length === 0 && (
                                                <div className="no-listings-found-message">
                                                    <h3>No listings found</h3>
                                                </div>
                                            )}
                                        </div>

                                        {/* Load More button - desktop + mobile list view */}
                                        {displayedListings.length > 0 && (
                                            <div className="load-more-container">
                                                {(displayedListings.length < filteredListings.length || hasMore) ? (
                                                    <button
                                                        className="load-more-button"
                                                        onClick={handleLoadMore}
                                                        disabled={loadingMore}
                                                    >
                                                        {loadingMore ? <div className="search-loading-dots"><span>.</span><span>.</span><span>.</span></div> : 'Load More'}
                                                    </button>
                                                ) : null}
                                            </div>
                                        )}
                                    </div>

                                    <div className="home-map-panel">
                                        <Suspense fallback={<div className="map-skeleton"></div>}>
                                            <MapboxListingsMap
                                                listings={mapListings}
                                                onMarkerClick={handleMarkerClick}
                                                selectedListingId={selectedListingId}
                                                onBoundsChange={handleMapBoundsChange}
                                                viewportResetKey={mapViewportKey}
                                                selectionResetKey={mobileMapDismissKey}
                                                onMapBackgroundClick={handleMobileSelectionClose}
                                                onPopupNavigate={navigateToListing}
                                                onGroupSelectionChange={handleMapGroupSelection}
                                                isMobileView={true}

                                                hasMobileSelectionTray={mobileSelectedListings.length > 0}
                                                loading={loading || isCheckingListings}
                                                focusLocation={mapFocusLocation}
                                                campusPins={mapCampusPins}
                                            />
                                        </Suspense>
                                        {mobileSelectedListings.length > 0 && (
                                            <div className="map-mobile-listings">
                                                {mobileSelectedListings.map((listing) => {
                                                    const listingIndex = filteredListings.findIndex((item) => item.id === listing.id);
                                                    return renderListingCard(listing, listingIndex, false, 'mapPreview');
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Mobile: Toggle between list and map */}
                                    {!showMapOnMobile && (
                                        <div className="home-list-panel" ref={listPanelRef}>
                                            <div ref={listTopRef} />
                                            {/* Listing count removed from here */}

                                            <div className="listings">
                                                {/* Show skeletons while loading, retrying, or checking for listings */}
                                                {(loading || (loadingMore && displayedListings.length === 0) || isCheckingListings) && displayedListings.length === 0 ? (
                                                    <>
                                                        {Array.from({ length: PAGE_SIZE }).map((_, index) => (
                                                            <div key={`skeleton-${index}`} className="listing-card-skeleton">
                                                                <div className="skeleton-image"></div>
                                                                <div className="skeleton-content">
                                                                    <div className="skeleton-title"></div>
                                                                    <div className="skeleton-subtitle"></div>
                                                                    <div className="skeleton-tags">
                                                                        <div className="skeleton-tag"></div>
                                                                        <div className="skeleton-tag"></div>
                                                                        <div className="skeleton-tag"></div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </>
                                                ) : null}
                                                {/* Render listings as soon as they're available */}
                                                {displayedListings.length > 0 ? (
                                                    <>
                                                        {displayedListings.map((listing, index) => renderListingCard(listing, index))}
                                                    </>
                                                ) : !loading && !isCheckingListings && displayedListings.length === 0 && (
                                                    <div className="no-listings-found-message">
                                                        <h3>No listings found</h3>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Load More button - desktop + mobile list view */}
                                            {displayedListings.length > 0 && (
                                                <div className="load-more-container">
                                                    {(displayedListings.length < filteredListings.length || hasMore) ? (
                                                        <button
                                                            className="load-more-button"
                                                            onClick={handleLoadMore}
                                                            disabled={loadingMore}
                                                        >
                                                            {loadingMore ? <div className="search-loading-dots"><span>.</span><span>.</span><span>.</span></div> : 'Load More'}
                                                        </button>
                                                    ) : null}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {showMapOnMobile && (
                                        <div className="home-map-panel home-map-panel-mobile">
                                            <Suspense fallback={<div className="map-skeleton"></div>}>
                                                <MapboxListingsMap
                                                    listings={mapListings}
                                                    onMarkerClick={handleMarkerClick}
                                                    selectedListingId={selectedListingId}
                                                    onBoundsChange={handleMapBoundsChange}
                                                    viewportResetKey={mapViewportKey}
                                                    selectionResetKey={mobileMapDismissKey}
                                                    onMapBackgroundClick={handleMobileSelectionClose}
                                                    onPopupNavigate={navigateToListing}
                                                    onGroupSelectionChange={handleMapGroupSelection}
                                                    isMobileView={true}
                                                    hasMobileSelectionTray={mobileSelectedListings.length > 0}
                                                    loading={loading || isCheckingListings}
                                                    focusLocation={mapFocusLocation}
                                                    campusPins={mapCampusPins}
                                                />
                                            </Suspense>
                                            {mobileSelectedListings.length > 0 && (
                                                <div className="map-mobile-listings">
                                                    {mobileSelectedListings.map((listing) => {
                                                        const listingIndex = filteredListings.findIndex((item) => item.id === listing.id);
                                                        return renderListingCard(listing, listingIndex, false, 'mapPreview');
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        <>
                            <div className="home-landing-page doodle-card">
                                <BackgroundDoodles />

                                <div className="home-landing-content">
                                    <h1 className="home-landing-headline" ref={landingHeadlineRef}>
                                        <span className="home-headline-line">The <span className="styled-accent">#1</span> subleasing platform</span>
                                        <span className="home-headline-line">designed for students and interns.</span>
                                    </h1>
                                    <div className="home-landing-hero-stack">
                                    <div className="home-landing-search-card search-card">
                                        <div className="home-landing-search-row-container">
                                            <div className="home-landing-top-row">
                                                <div className="filter-group home-landing-university-search-group">
                                                    <UniversitySearch
                                                        value={pendingUniversity}
                                                        onChange={setPendingUniversity}
                                                        onInputChange={handleLandingUniversityInputChange}
                                                        onUnsupportedSearch={(query) => {
                                                            setIsUnsupportedUniversity(true);
                                                            setShowNoListingsPopup(true);
                                                            setVotePrepopulatedUniversity(query || '');
                                                        }}
                                                        onValidationChange={setIsUniversityInputValid}
                                                        onSearch={() => {
                                                            triggerLandingSearch();
                                                        }}
                                                        placeholder="Search University or City"
                                                        className="home-landing-university-search"
                                                        supportedUniversityIds={SUPPORTED_UNIVERSITY_IDS}
                                                        disableCapsule={true}
                                                        showCities={true}
                                                    />
                                                </div>
                                                <div className="filter-group home-landing-date-picker-group">
                                                    <DateRangePicker
                                                        className="home-landing-date-picker"
                                                        startDate={pendingStartDate}
                                                        endDate={pendingEndDate}
                                                        disabled={pendingTerms.length > 0}
                                                        hideSummary={true}
                                                        showIconAlways={true}
                                                        onChange={(start, end) => {
                                                            setPendingStartDate(start);
                                                            setPendingEndDate(end);
                                                            if (start || end) {
                                                                setPendingTerms([]);
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="home-landing-bottom-row">
                                                <button
                                                    className="home-landing-search-button"
                                                    onClick={async () => {
                                                        const { didTrigger, universityIdToSearch } = triggerLandingSearch();
                                                        if (!didTrigger) return;


                                                        // Track the search in admin activity
                                                        try {
                                                            const universityToTrack = universityIdToSearch || pendingUniversity || selectedUniversity;
                                                            if (universityToTrack || pendingTerms.length > 0) {
                                                                const selectedUni = universities.find(u => u.id.toString() === universityToTrack);
                                                                await fetch(API_ENDPOINTS.ADMIN.TRACK_SEARCH, {
                                                                    method: 'POST',
                                                                    headers: {
                                                                        'Content-Type': 'application/json',
                                                                    },
                                                                    body: JSON.stringify({
                                                                        university_id: universityToTrack,
                                                                        university_name: selectedUni?.name || universityToTrack,
                                                                        terms: pendingTerms
                                                                    })
                                                                }).catch(err => console.log('Search tracking failed:', err));
                                                            }
                                                        } catch (err) {
                                                            console.log('Search tracking error:', err);
                                                        }
                                                    }}
                                                >
                                                    Search
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    </div>
                                </div>
                            </div>

                        {(loadingLandingFeatured || landingFeaturedListings.length > 0) && (
                            <div className="info-card landing-featured-card">
                                <h4 className="featured-section-title-small">Featured</h4>
                                <div className="landing-featured-listings">
                                    {loadingLandingFeatured && landingFeaturedListings.length === 0
                                        ? [0, 1, 2].map((i) => (
                                            <div key={`featured-skeleton-${i}`} className="listing-card--featured-landing listing-card--featured-landing-skeleton" aria-hidden="true">
                                                <div className="listing-card--featured-landing__media featured-skeleton-block" />
                                                <div className="listing-card--featured-landing__body">
                                                    <div className="featured-skeleton-line featured-skeleton-line--title" />
                                                    <div className="featured-skeleton-line featured-skeleton-line--meta" />
                                                </div>
                                            </div>
                                        ))
                                        : landingFeaturedListings.map((listing, index) => (
                                            <div
                                                key={listing.id}
                                                className="landing-featured-item-fade"
                                            >
                                                {renderListingCard(listing, index, false, 'featuredLanding')}
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}

                        <div className="info-card">
                                <div className="info-card-grid">
                                    <div className="info-card-item">
                                        <h3>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                                                <circle cx="8" cy="10" r="1.5" fill="currentColor" />
                                                <circle cx="16" cy="10" r="1.5" fill="currentColor" />
                                                <path d="M8 14c0 2 1.5 4 4 4s4-2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                            List Your Place
                                        </h3>
                                        <p>
                                            Create an account and <span className="styled-accent">have it up in minutes</span> with photos, dates, and all the deets.
                                        </p>

                                    </div>
                                    <div className="info-card-item">
                                        <h3>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                                                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                                                <path d="m20 20-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                            Find Your Match
                                        </h3>
                                        <p>
                                            Browse <span className="styled-accent">subleases by university, date, price, bedrooms, and must-haves</span>. Find exactly what fits your schedule and budget no more scrolling through endless listings.
                                        </p>

                                    </div>
                                    <div className="info-card-item">
                                        <h3>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" />
                                                <line x1="8" y1="9" x2="16" y2="9" stroke="currentColor" strokeWidth="2" />
                                                <line x1="8" y1="13" x2="14" y2="13" stroke="currentColor" strokeWidth="2" />
                                            </svg>
                                            Chat Directly
                                        </h3>
                                        <p>
                                            Message other students and interns <span className="styled-accent">right on the platform</span>. No awkward emails or phone calls, just simple, straightforward conversations.
                                        </p>
                                    </div>
                                    <div className="info-card-item">
                                        <h3>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="m9 11 2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            Verified Users
                                        </h3>
                                        <p>
                                            Connect with <span className="styled-accent">peace of mind</span>. We verify student or government IDs to ensure you are communicating with real, verified people.
                                        </p>
                                    </div>
                                </div>
                            </div>


                            <div className="home-brand-text">
                                USUBLEASE
                            </div>
                        </>
                    )}

                    {/* Mobile Modal Overlay */}
                    {/* (Disabled) duplicate mobile filters modal kept for reference */}
                    {showFiltersDropdown && isMobile && false && (
                        <div className="filters-modal-overlay" onClick={() => setShowFiltersDropdown(false)}>
                            <div className="filters-modal" onClick={(e) => e.stopPropagation()}>
                                <div className="filter-section">
                                    <label className="filter-section-label">Sort by</label>
                                    <div className="sort-buttons">
                                        <button
                                            className={`sort-button ${priceSort === '' ? 'selected' : ''}`}
                                            onClick={() => setPriceSort('')}
                                        >
                                            Recent
                                        </button>
                                        <button
                                            className={`sort-button ${priceSort === 'low-to-high' ? 'selected' : ''}`}
                                            onClick={() => setPriceSort('low-to-high')}
                                        >
                                            Price ↑
                                        </button>
                                        <button
                                            className={`sort-button ${priceSort === 'high-to-low' ? 'selected' : ''}`}
                                            onClick={() => setPriceSort('high-to-low')}
                                        >
                                            Price ↓
                                        </button>
                                    </div>
                                </div>

                                <div className="filter-section">
                                    <label className="filter-section-label">Date Range</label>
                                    <DateRangePicker
                                        startDate={selectedStartDate}
                                        endDate={selectedEndDate}
                                        onChange={(start, end) => {
                                            setSelectedStartDate(start);
                                            setSelectedEndDate(end);
                                        }}
                                    />
                                </div>

                                <div className="filter-option">
                                    <label htmlFor="modal-unit-type-filter">Unit Type</label>
                                    <select
                                        id="modal-unit-type-filter"
                                        value={selectedUnitType}
                                        onChange={(e) => setSelectedUnitType(e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="studio">Studio</option>
                                        <option value="1x1">1x1</option>
                                        <option value="1x2">1x2</option>
                                        <option value="2x1">2x1</option>
                                        <option value="2x2">2x2</option>
                                        <option value="3x1">3x1</option>
                                        <option value="3x2">3x2</option>
                                        <option value="3x3">3x3</option>
                                        <option value="4x2">4x2</option>
                                        <option value="4x3">4x3</option>
                                        <option value="4x4">4x4</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>

                                <div className="filter-section">
                                    <label className="filter-section-label">Gender Preference</label>
                                    <div className="filter-buttons-row">
                                        <button
                                            className={`filter-button ${genderPreference === '' ? 'selected' : ''}`}
                                            onClick={() => setGenderPreference('')}
                                        >
                                            All
                                        </button>
                                        <button
                                            className={`filter-button ${genderPreference === 'male_preferred' ? 'selected' : ''}`}
                                            onClick={() => setGenderPreference('male_preferred')}
                                        >
                                            Males
                                        </button>
                                        <button
                                            className={`filter-button ${genderPreference === 'female_preferred' ? 'selected' : ''}`}
                                            onClick={() => setGenderPreference('female_preferred')}
                                        >
                                            Females
                                        </button>
                                    </div>
                                </div>

                                <div className="filter-section">
                                    <label className="filter-section-label">Tags</label>
                                    <div className="filter-buttons-row">
                                        <button
                                            className={`filter-button ${petsAllowed ? 'selected' : ''}`}
                                            onClick={() => setPetsAllowed(!petsAllowed)}
                                        >
                                            Pets
                                        </button>
                                        <button
                                            className={`filter-button ${parkingRequired ? 'selected' : ''}`}
                                            onClick={() => setParkingRequired(!parkingRequired)}
                                        >
                                            Parking
                                        </button>
                                        <button
                                            className={`filter-button ${gymRequired ? 'selected' : ''}`}
                                            onClick={() => setGymRequired(!gymRequired)}
                                        >
                                            Gym
                                        </button>
                                        <button
                                            className={`filter-button ${poolRequired ? 'selected' : ''}`}
                                            onClick={() => setPoolRequired(!poolRequired)}
                                        >
                                            Pool
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                {!hasActiveSearch && (
                    <Suspense fallback={null}>
                        <Footer />
                    </Suspense>
                )}

                {/* No Listings Popup - Shows on landing page only - Outside browse-page for proper z-index */}
                {showNoListingsPopup && (
                    <div className={`usub-no-listings-modal-backdrop-glass-capsule-2026 ${isNoListingsPopupFadingOut ? 'fade-out' : ''}`}>
                        <div className="usub-no-listings-glass-capsule-popup-container-2026">
                            <div className="usub-no-listings-glass-capsule-content-2026">
                                <span className="usub-no-listings-capsule-title-2026">
                                    {isUnsupportedUniversity
                                        ? 'This location is not supported yet'
                                        : 'No listings yet, be the first!'}
                                </span>
                                {isUnsupportedUniversity && (
                                    <button 
                                        className="usub-vote-capsule-button-2026"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowNoListingsPopup(false);
                                            setShowVoteModal(true);
                                        }}
                                    >
                                        Vote to add
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {showValidationError && (
                    <div className="usub-no-listings-modal-backdrop-glass-capsule-2026">
                        <div className="usub-no-listings-glass-capsule-popup-container-2026">
                            <div className="usub-no-listings-glass-capsule-content-2026">
                                <span className="usub-no-listings-capsule-title-2026">
                                    This location is not supported yet
                                </span>
                                <button 
                                    className="usub-vote-capsule-button-2026"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowValidationError(false);
                                        setShowVoteModal(true);
                                    }}
                                >
                                    Vote to add
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showVoteThanksPopup && (
                    <div className={`usub-no-listings-modal-backdrop-glass-capsule-2026 ${isVoteThanksFadingOut ? 'fade-out' : ''}`}>
                        <div className="usub-no-listings-glass-capsule-popup-container-2026">
                            <div className="usub-no-listings-glass-capsule-content-2026">
                                <span className="usub-no-listings-capsule-title-2026">
                                    Thanks, vote received!
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                <VoteModal
                    isOpen={showVoteModal}
                    onClose={() => setShowVoteModal(false)}
                    onSuccess={() => {
                        setShowVoteThanksPopup(true);
                        setIsVoteThanksFadingOut(false);
                    }}
                    prepopulatedUniversity={votePrepopulatedUniversity}
                />
            </div>
        </>
    );
};

export default Home;
